import React, { useState, useEffect, useLayoutEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { ACTION_LABELS, roleLabel, formatRequestor } from './theme';
import RequestChainCard from './RequestChainCard';
import { PRIORITY_OPTIONS, PRIORITY_SLA, priorityBadgeClass } from './priority';
import BillingPanel from './BillingPanel';
import CostingPanel from './CostingPanel';
import TagWorkPricingCard from './TagWorkPricingCard';
import { AuthImage } from './AuthImage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Wrench, User, Building2, MapPin, Calendar, Flag, FileText,
  Paperclip, PauseCircle, XCircle, ClipboardList, AlertTriangle, Clock, Truck, CheckCircle2,
  Download, Send, BadgeCheck, Upload, Camera, Receipt, Briefcase, DollarSign, Link2,
  ChevronDown, ChevronRight, Save, X, RotateCcw, UserPlus, Search, Lock, Trash2,
} from 'lucide-react';

import { WorkOrderPurgeDialog, showPurgeUndoToast } from '@/components/operations/WorkOrderPurgeDialog';

import { fmtDate, fmtDateTime } from '@/lib/dates';
const fmtLabelDates = (s) => (s || '').replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, mo, d) => `${mo}/${d}/${y.slice(2)}`);
const fmt = (d, withTime) => (d ? ((withTime ? fmtDateTime(d) : fmtDate(d)) || '—') : '—');
const val = (v) => (v === null || v === undefined || v === '' ? '—' : v);

const FULFILLMENT_OPTIONS = ['Unassigned', 'TFS Internal Personnel', 'Assigned Location Personnel', 'TFS Subcontractor', 'Third-Party Service Provider'];
const STATUS_OPTIONS = ['Open - Unassigned', 'Assigned', 'Scheduled', 'Dispatched', 'En Route', 'In Progress', 'Awaiting Authorization', 'Awaiting Administrative Closeout', 'Completed', 'On Hold', 'Cancelled'];
const COMPENSATION_CLASSES = ['No Additional Payout', 'Additional Payout Required', 'To Be Determined'];
const DISPATCH_METHODS = ['Phone', 'Text', 'Email', 'In Person', 'Other'];
const RELATIONSHIP_TYPES = ['Location Employee', 'Location Contractor', 'Vendor Staff', 'Other'];

const statusColor = (s) => {
  if (s === 'Completed') return 'bg-green-100 text-green-800 border-green-200';
  if (s === 'Assigned') return 'bg-green-100 text-green-800 border-green-200';
  if (s === 'Awaiting Administrative Closeout') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
  if (s === 'On Hold') return 'bg-slate-200 text-slate-700 border-slate-300';
  if (s === 'Cancelled') return 'bg-red-100 text-red-700 border-red-200';
  if (s && s.includes('Unassigned')) return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const Field = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2 text-sm sm:text-[15px]">
    {Icon && <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />}
    <div className="min-w-0"><div className="text-[11px] sm:text-xs text-slate-400">{label}</div><div className="text-slate-700 break-words">{children}</div></div>
  </div>
);

const BannerField = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-[#A3D65C]">{label}</div>
    <div className="text-[13px] text-slate-100 leading-tight truncate">{children}</div>
  </div>
);
const CustomerLogoImg = ({ customerId, token }) => {
  const [failed, setFailed] = useState(false);
  if (!customerId || failed) return <Building2 size={18} className="text-slate-300" />;
  return <img src={`${API}/customers/${customerId}/logo?auth=${token}`} onError={() => setFailed(true)} alt="" className="h-6 w-auto max-w-[96px] object-contain" />;
};
const shortJobTitle = (t) => ((t || '').replace(/^above[- ]?scope\s*:?\s*/i, '').trim() || t || '—');

const WorkOrderDetail = ({ workOrderId, canManage, onBack, promptAssignOnLoad = false, onOpenRequest }) => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const canPurge = user?.role === 'owner' || (user?.capabilities || []).includes('admin.controls');
  const [purgeOpen, setPurgeOpen] = useState(false);

  const [wo, setWo] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fieldUsers, setFieldUsers] = useState([]);

  const [dialog, setDialog] = useState(null); // 'priority' | 'assignment' | 'status'
  const [pr, setPr] = useState('');
  const [asg, setAsg] = useState({ fulfillment_type: 'Unassigned', assigned_to: '', assigned_personnel_id: '', service_provider_id: '', compensation_classification: '', payout_amount: '', payout_basis: '', payout_approved_by: '', payroll_notes: '', responsible_manager_id: '' });
  const [personnel, setPersonnel] = useState([]);
  const [managers, setManagers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [quickAdd, setQuickAdd] = useState(null); // null | {name, employee_identifier, phone, email, relationship_type, has_rout_access}
  const [disp, setDisp] = useState({ dispatch_method: '', scheduled_date: '', service_window: '', notes: '' });
  const [sts, setSts] = useState({ status: '', notes: '' });
  const [sch, setSch] = useState({ scheduled_date: '', service_window: '', required_completion_date: '', scheduling_notes: '' });
  const [close, setClose] = useState({ billable: false, notes: '' });
  const [notify, setNotify] = useState({ to: '', cc: '', subject: '', message: '', waiver: '', preview: null, loading: false });
  const [reportLang, setReportLang] = useState('en'); // recipient's completion-report language (EN/ES)
  const [assignPrompted, setAssignPrompted] = useState(false);
  const [costForm, setCostForm] = useState({ client_charge: '', service_fee_type: 'pre_established', additional_charges: '', additional_charges_note: '' });
  const [chainOpen, setChainOpen] = useState(false);
  const [asgSearch, setAsgSearch] = useState('');
  const isOwner = user?.role === 'owner';
  const [ovr, setOvr] = useState(null); // null | { new_amount, new_scope, reason }
  const [ovrBusy, setOvrBusy] = useState(false);
  const submitOverride = async () => {
    if (!ovr?.reason?.trim()) { toast.error('A written explanation is required'); return; }
    setOvrBusy(true);
    try {
      const body = { reason: ovr.reason.trim() };
      if (ovr.new_amount !== '' && ovr.new_amount != null) body.new_amount = Number(ovr.new_amount);
      if (ovr.new_scope?.trim()) body.new_scope = ovr.new_scope.trim();
      await axios.patch(`${API}/work-orders/${workOrderId}/pev-pricing-override`, body, authHeaders);
      toast.success('Override recorded — renewed customer authorization flagged');
      setOvr(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Override failed'); }
    finally { setOvrBusy(false); }
  };
  const woLocked = !!wo && (['Completed', 'Closed', 'Cancelled', 'Awaiting Administrative Closeout'].includes(wo.status) || (!!wo.billing_status && wo.billing_status !== 'Not Submitted'));
  const [mats, setMats] = useState([]);
  const [matStatus, setMatStatus] = useState('');
  // ROUT → Payroll handoff
  const [payrollPrev, setPayrollPrev] = useState(null);
  const [payrollForm, setPayrollForm] = useState({});
  const [payrollBusy, setPayrollBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [mDetails, setMDetails] = useState(false);
  // ---- Inline page-wide edit mode (Global / General Administrators) ----
  // Per-section inline editing — only one section unlocks at a time (Slice D).
  const [editSection, setEditSection] = useState(null); // null | 'work' | 'assignment' | 'billing'
  const editing = editSection !== null;        // any section currently in edit mode
  const editingWork = editSection === 'work';
  const editingAssignment = editSection === 'assignment';
  const editingBilling = editSection === 'billing';
  const [draft, setDraft] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const costingRef = useRef(null);
  const dset = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // Keep the user's scroll position / working section stable across Edit<->read toggles.
  const scrollAnchor = useRef(null);
  const captureAnchor = () => {
    try {
      const refY = 210; // just below the sticky top bar (63px) + banner
      const secs = Array.from(document.querySelectorAll('[data-testid^="wo-section-"]'));
      let chosen = null;
      for (const el of secs) { if (el.getBoundingClientRect().bottom > refY) { chosen = el; break; } }
      if (!chosen && secs.length) chosen = secs[secs.length - 1];
      scrollAnchor.current = chosen ? { el: chosen, top: chosen.getBoundingClientRect().top } : null;
    } catch { scrollAnchor.current = null; }
  };

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/work-orders/${workOrderId}`, authHeaders),
        axios.get(`${API}/work-orders/${workOrderId}/activity`, authHeaders),
      ]);
      setWo(a.data); setActivity(b.data || []);
    } catch (e) { toast.error('Failed to load work order'); }
    finally { setLoading(false); }
  }, [workOrderId]);

  useEffect(() => { load(); }, [load]);
  // After an Edit<->read layout change, re-anchor scroll so the working section stays put.
  useLayoutEffect(() => {
    const a = scrollAnchor.current;
    if (!a || !a.el || !a.el.isConnected) return;
    const delta = a.el.getBoundingClientRect().top - a.top;
    if (Math.abs(delta) > 1) window.scrollBy(0, delta);
    scrollAnchor.current = null;
  }, [editSection]);
  useEffect(() => {
    if (promptAssignOnLoad && canManage && wo && !assignPrompted &&
        (wo.status === 'Open - Unassigned' || wo.assignment_status === 'Unassigned')) {
      setAssignPrompted(true);
      setDialog('created-unassigned');
    }
  }, [wo, promptAssignOnLoad, canManage, assignPrompted]);
  useEffect(() => {
    if (canManage) {
      axios.get(`${API}/work-orders/field-users`, authHeaders).then((r) => setFieldUsers(r.data || [])).catch(() => {});
      axios.get(`${API}/service-providers`, { ...authHeaders, params: { active_only: true } }).then((r) => setProviders(r.data || [])).catch(() => {});
    }
  }, [canManage]);

  const post = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/${workOrderId}/${path}`, body, authHeaders);
      toast.success(okMsg); setDialog(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const loadPayrollPreview = async () => {
    setPayrollBusy(true);
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/payroll-preview`, authHeaders);
      setPayrollPrev(r.data);
      const now = new Date();
      const form = {};
      (r.data.workers || []).forEach((w) => {
        form[w.worker_key] = {
          actual_hours: w.default_hours ?? '',
          cost_classification: 'additional_work_order_pay',
          approved_payout: w.default_payout ?? '',
          payout_basis: '', note: '',
          payment_timing: 'next_payroll',
          specified_group: 'monthly', specified_year: now.getFullYear(), specified_month: now.getMonth() + 1,
        };
      });
      setPayrollForm(form);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load payroll preview'); }
    finally { setPayrollBusy(false); }
  };

  const setPf = (key, patch) => setPayrollForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const submitPayroll = async () => {
    const workers = (payrollPrev?.workers || []).map((w) => {
      const f = payrollForm[w.worker_key] || {};
      const base = { worker_key: w.worker_key, actual_hours: Number(f.actual_hours), cost_classification: f.cost_classification };
      if (f.cost_classification === 'additional_work_order_pay') {
        base.approved_payout = Number(f.approved_payout);
        base.payout_basis = f.payout_basis || null;
        base.note = f.note || null;
        base.payment_timing = f.payment_timing;
        if (f.payment_timing === 'specified_future') {
          base.specified_group = f.specified_group;
          base.specified_year = Number(f.specified_year);
          base.specified_month = Number(f.specified_month);
        }
      }
      return base;
    });
    setPayrollBusy(true);
    try {
      const r = await axios.post(`${API}/work-orders/${workOrderId}/ready-for-payroll`, { workers }, authHeaders);
      toast.success(`Handed to Payroll — ${r.data.items_created} pay item(s) created`);
      setDialog(null); setPayrollPrev(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payroll handoff failed'); }
    finally { setPayrollBusy(false); }
  };

  const loadPersonnel = useCallback(async () => {
    if (!wo?.customer_id) return;
    try {
      const r = await axios.get(`${API}/location-personnel`, { ...authHeaders, params: { customer_id: wo.customer_id, location_id: wo.location_id } });
      setPersonnel(r.data || []);
    } catch (e) { /* non-blocking */ }
  }, [wo?.customer_id, wo?.location_id]);

  const loadManagers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users`, authHeaders);
      // Responsible Operations Managers are authorized TFS staff (owner/admin/manager) — never clients/field-only.
      setManagers((r.data || []).filter((u) => ['owner', 'admin', 'manager'].includes(u.role)));
    } catch (e) { /* non-blocking */ }
  }, []);

  const openAssignment = () => {
    setAsg({
      fulfillment_type: wo.fulfillment_type || 'Unassigned',
      assigned_to: wo.assigned_to || '',
      assigned_personnel_id: wo.assigned_personnel_id || '',
      service_provider_id: wo.service_provider_id || '',
      compensation_classification: wo.compensation_classification || '',
      payout_amount: wo.payout_amount ?? '',
      payout_basis: wo.payout_basis || '',
      payout_approved_by: wo.payout_approved_by || '',
      payroll_notes: wo.payroll_notes || '',
      responsible_manager_id: wo.responsible_manager_id || '',
    });
    setQuickAdd(null);
    loadPersonnel();
    loadManagers();
    setDialog('assignment');
  };

  // Dispatch readiness — the requirements the BACKEND enforces at Dispatch (not at assignment).
  // Returns a list of human-readable gaps; empty means ready to dispatch.
  const dispatchGaps = (w) => {
    if (!w) return [];
    const gaps = [];
    if (!(w.assignment_status === 'Assigned' && w.assigned_to_name)) gaps.push('a named T.E.A.M. member / provider');
    const isPev = !!(w.pev_inspection_number || w.created_via === 'pev_authorization');
    const isSr = w.source_type === 'service_request';
    if (isPev) {
      if (!w.pev_inspection_number) gaps.push('the originating Property Evaluation #');
      if (!(w.client_authorized || w.customer_authorization_ref)) gaps.push('customer authorization');
    } else if (isSr) {
      if (!w.rout_service_request_number) gaps.push('the linked ROUT Service Request #');
    } else {
      if (!w.request_source) gaps.push('the request source');
      if (!w.responsible_manager_name) gaps.push('the Responsible TFS Operations Manager');
      if (!(w.required_completion_date || w.required_service_date)) gaps.push('the required completion date');
    }
    if (!w.priority_level) gaps.push('the ROUT priority');
    if (!(w.scheduled_date || w.service_window)) gaps.push('a scheduled date or service window');
    return gaps;
  };

  const saveAssignment = async () => {
    const body = {
      fulfillment_type: asg.fulfillment_type,
      assigned_to: asg.fulfillment_type === 'TFS Internal Personnel' ? (asg.assigned_to || null) : null,
      responsible_manager_id: asg.responsible_manager_id || null,
      responsible_manager_name: asg.responsible_manager_id
        ? (managers.find((m) => m.id === asg.responsible_manager_id)?.name || null) : null,
    };
    if (asg.fulfillment_type === 'TFS Subcontractor' || asg.fulfillment_type === 'Third-Party Service Provider') {
      body.service_provider_id = asg.service_provider_id || null;
    }
    if (asg.fulfillment_type === 'Assigned Location Personnel') {
      body.assigned_personnel_id = asg.assigned_personnel_id || null;
      body.compensation_classification = asg.compensation_classification || null;
      body.payout_amount = asg.payout_amount ? parseFloat(asg.payout_amount) : null;
      body.payout_basis = asg.payout_basis || null;
      body.payout_approved_by = asg.payout_approved_by || null;
      body.payroll_notes = asg.payroll_notes || null;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/work-orders/${workOrderId}/assignment`, body, authHeaders);
      setDialog(null);
      await load();
      const fresh = r.data || {};
      const name = fresh.assigned_to_name;
      if (name) {
        const gaps = dispatchGaps(fresh);
        if (gaps.length) {
          toast.success(`Assigned to ${name}. Complete the remaining dispatch requirements before issuing the work order.`, { duration: 6000 });
        } else {
          toast.success(`Assigned to ${name}. Ready to dispatch.`);
        }
      } else {
        toast.success('Assignment updated');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const submitQuickAdd = async () => {
    if (!quickAdd?.name?.trim()) { toast.error('Employee name is required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/location-personnel`, {
        customer_id: wo.customer_id, location_id: wo.location_id,
        name: quickAdd.name.trim(), employee_identifier: quickAdd.employee_identifier || null,
        phone: quickAdd.phone || null, email: quickAdd.email || null,
        relationship_type: quickAdd.relationship_type || 'Location Employee',
        has_rout_access: !!quickAdd.has_rout_access,
      }, authHeaders);
      toast.success('Location employee added');
      await loadPersonnel();
      setAsg((a) => ({ ...a, assigned_personnel_id: r.data.id }));
      setQuickAdd(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add employee'); }
    finally { setBusy(false); }
  };

  const openDispatch = () => {
    setDisp({ dispatch_method: '', scheduled_date: wo.scheduled_date || '', service_window: wo.service_window || '', notes: '' });
    setDialog('dispatch');
  };
  const submitDispatch = () => {
    const body = { scheduled_date: disp.scheduled_date || null, service_window: disp.service_window || null, notes: disp.notes || null };
    if (!(wo.assigned_personnel_has_rout || wo.assigned_to)) body.dispatch_method = disp.dispatch_method || null;
    post('dispatch', body, 'Work order dispatched');
  };

  // ---- Operational closeout: media, personnel closeout, billing disposition ----
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState('');
  const [closeout, setCloseout] = useState({ work_performed_by: '', actual_service_date: '', actual_start_time: '', actual_end_time: '', actual_total_hours: '', work_performed: '', closeout_notes: '', verified_by_name: '', verification_method: '', follow_up_required: false, follow_up_date: '' });
  const [dispo, setDispo] = useState({ disposition: '', reason: '' });
  const isGA = ['owner', 'admin', 'manager'].includes(user?.role);
  // Direct inline editing / correction of a Work Order is limited to Global (owner)
  // and General (admin) Administrators.
  const canEdit = ['owner', 'admin'].includes(user?.role);

  const loadMedia = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/media`, authHeaders);
      setMedia(r.data || []);
    } catch (e) { /* ignore */ }
  }, [workOrderId]);
  useEffect(() => { loadMedia(); }, [loadMedia]);
  const [woDocCat, setWoDocCat] = useState('Receipt');
  const deleteMedia = async (id) => {
    const reason = window.prompt('Reason for removing this attachment? (recorded in audit history)');
    if (reason === null) return;
    try {
      await axios.delete(`${API}/work-orders/${workOrderId}/media/${id}?reason=${encodeURIComponent(reason)}`, authHeaders);
      toast.success('Attachment removed'); await loadMedia(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };

  const uploadMedia = async (files, category, docCategory) => {
    const list = files && files.length ? Array.from(files) : (files ? [files] : []);
    if (!list.length) return;
    setUploading(category);
    let ok = 0, fail = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', category);
        if (docCategory) fd.append('doc_category', docCategory);
        fd.append('work_performed_by', wo.assigned_to_name || '');
        await axios.post(`${API}/work-orders/${workOrderId}/media`, fd, { headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } });
        ok += 1;
      } catch (e) { fail += 1; toast.error(`${file.name}: ${e.response?.data?.detail || 'upload failed'}`); }
    }
    if (ok) toast.success(`${ok} ${category} file${ok > 1 ? 's' : ''} uploaded${fail ? `, ${fail} failed` : ''}`);
    await loadMedia(); await load();
    setUploading('');
  };

  const regenerateReport = async () => {
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/completion-report.pdf`, { ...authHeaders, params: { lang: reportLang }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success('Completion Report regenerated (prior versions superseded, not deleted)');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate report'); }
  };

  const viewDraftReport = async () => {
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/completion-report.pdf`, { ...authHeaders, params: { draft: true, lang: reportLang }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success('Draft report opened (internal review — not delivered)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate draft report'); }
  };

  const submitCloseout = async () => {
    const c = closeout;
    if (!c.actual_service_date || !c.work_performed || !c.verification_method) { toast.error('Actual service date, work performed and verification method are required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/${workOrderId}/personnel-closeout`, {
        work_performed_by: c.work_performed_by || null,
        actual_service_date: c.actual_service_date,
        actual_start_time: c.actual_start_time || null,
        actual_end_time: c.actual_end_time || null,
        actual_total_hours: c.actual_total_hours ? parseFloat(c.actual_total_hours) : null,
        work_performed: c.work_performed, closeout_notes: c.closeout_notes || null,
        verified_by_name: c.verified_by_name || null, verification_method: c.verification_method,
        follow_up_required: !!c.follow_up_required, follow_up_date: c.follow_up_date || null,
      }, authHeaders);
      toast.success('Closeout submitted'); setDialog(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Closeout failed'); }
    finally { setBusy(false); }
  };

  const submitDispo = async () => {
    if (!dispo.disposition) { toast.error('Select a billing disposition'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/${workOrderId}/billing-disposition`, { disposition: dispo.disposition, reason: dispo.reason || null }, authHeaders);
      toast.success('Billing disposition recorded'); setDialog(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const mediaByCat = (cat) => media.filter((m) => m.category === cat);

  const downloadReport = async () => {
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/completion-report.pdf`,
        { ...authHeaders, params: { lang: reportLang }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = url; a.download = `Completion_${wo.tfs_work_order_number || 'WO'}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      load();
    } catch (e) {
      toast.error('Could not open the Completion Report');
    }
  };

  const openNotify = async () => {
    setNotify({ to: '', cc: '', subject: '', message: '', waiver: '', preview: null, loading: true });
    setDialog('notify');
    try {
      const { data } = await axios.get(`${API}/work-orders/${workOrderId}/notify-preview`, authHeaders);
      const ref = data.references || {};
      const fbUrl = `${window.location.origin}${data.feedback_path}`;
      const subject = `Completed Work — ${data.job_title || 'Service'} | WO ${ref.tfs_work_order_number || ''}` +
        (ref.service_request_number ? ` | SR ${ref.service_request_number}` : '');
      const message =
        `Hello ${data.requester_first_name || 'there'},\n\n` +
        `We have completed ${data.work_summary || 'the requested work'}${data.service_location ? ` at ${data.service_location}` : ''}. ` +
        `Please see the attached completion report and supporting photos.\n\n` +
        `If anything needs adjustment or does not meet your expectations, please reply so we can address it.\n\n` +
        `How did we do? Please rate our timeliness, quality of work, and professionalism using the feedback link below, or share your comments by replying.\n` +
        `Rate Our Service: ${fbUrl}\n\n` +
        `Thank you for your business and the opportunity to be of service. We’re here whenever you need us.\n\n` +
        `Total Facility Solutions, Inc.`;
      setNotify({
        to: (data.recipients?.to || []).join(', '),
        cc: (data.recipients?.cc || []).join(', '),
        subject, message, waiver: '', preview: data, loading: false,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not prepare the customer email');
      setDialog(null);
    }
  };

  const sendNotify = async () => {
    const to = notify.to.split(',').map((s) => s.trim()).filter(Boolean);
    const cc = notify.cc.split(',').map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/work-orders/${workOrderId}/notify-customer`,
        { recipients: to, cc, subject: notify.subject, message: notify.message, photo_waiver_reason: notify.waiver || null, report_language: reportLang }, authHeaders);
      if (data.sent) {
        toast.success('Completion Report sent to customer');
      } else if (data.delivery_state === 'delivery_not_configured') {
        toast.warning('Prepared — email delivery is not configured yet, so nothing was sent.');
      } else {
        toast.error('Delivery failed — please try again once email is configured.');
      }
      setDialog(null); await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 422 && d?.code === 'evidence_required') {
        toast.error(`Photos required: ${(d.missing || []).join(' and ')}. Add photos or record a Global-Admin exception.`);
      } else if (e.response?.status === 422 && d?.code === 'work_performed_required') {
        toast.error(d.message || "Add 'Work Actually Performed' before sending.");
        setDialog(null);
        if (canEdit) enterEdit('work');
      } else {
        toast.error((typeof d === 'string' ? d : d?.message) || 'Could not notify customer');
      }
    } finally { setBusy(false); }
  };

  // ---- Inline edit mode: enter / cancel / save ----
  const enterEdit = (section = 'work') => {
    captureAnchor();
    setDraft({
      job_name: wo.job_name || '',
      trade_type: wo.trade_type || '',
      status: wo.status || '',
      requestor_name: wo.requestor_name || '',
      requestor_email: wo.requestor_email || '',
      requestor_phone: wo.requestor_phone || '',
      customer_work_order_number: wo.customer_reference || wo.customer_work_order_number || '',
      customer_po_number: wo.customer_po_number || '',
      description_of_work: wo.description_of_work || wo.description || '',
      work_performed: wo.work_performed || wo.description_of_work_performed || '',
      special_instructions: wo.special_instructions || '',
      internal_notes: wo.internal_notes || '',
      priority_level: wo.priority_level || '',
      date_received: (wo.date_received || '').slice(0, 10),
      required_service_date: (wo.required_service_date || '').slice(0, 10),
      required_completion_date: (wo.required_completion_date || '').slice(0, 10),
      scheduled_date: (wo.scheduled_date || '').slice(0, 10),
      service_window: wo.service_window || '',
      main_contact_name: wo.main_contact_name || wo.contact_person || '',
      main_contact_email: wo.main_contact_email || wo.contact_email || '',
      main_contact_phone: wo.main_contact_phone || wo.contact_phone || '',
      local_contact_name: wo.local_contact_name || '',
      local_contact_phone: wo.local_contact_phone || '',
      service_location: wo.service_location || '',
      service_address: wo.service_address || '',
      service_city: wo.service_city || '',
      service_state: wo.service_state || '',
      service_zip: wo.service_zip || '',
      client_charge: (wo.client_charge ?? wo.service_call_amount) ?? '',
      service_fee_type: wo.service_fee_type || 'pre_established',
      additional_charges: (wo.additional_charges ?? wo.additional_approved_charges) ?? '',
      additional_charges_note: wo.additional_charges_note || '',
    });
    setAsg({
      fulfillment_type: wo.fulfillment_type || 'Unassigned',
      assigned_to: wo.assigned_to || '',
      assigned_personnel_id: wo.assigned_personnel_id || '',
      service_provider_id: wo.service_provider_id || '',
      compensation_classification: wo.compensation_classification || '',
      payout_amount: wo.payout_amount ?? '', payout_basis: wo.payout_basis || '',
      payout_approved_by: wo.payout_approved_by || '', payroll_notes: wo.payroll_notes || '',
      responsible_manager_id: wo.responsible_manager_id || '',
    });
    setQuickAdd(null);
    loadPersonnel();
    loadManagers();
    setManageOpen(false);
    setEditSection(section);
  };

  const cancelEdit = () => { captureAnchor(); setEditSection(null); setDraft({}); setQuickAdd(null); };

  // Compact Save/Cancel pair used by the per-section (Assignment / Billing) editors.
  const SectionEditActions = ({ testid }) => (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Button size="sm" className="h-7 bg-[#8FC93A] px-3 text-xs font-semibold text-[#0E2A47] hover:bg-[#7fb82f]" disabled={savingEdit} onClick={saveEdit}><Save size={12} className="mr-1" />{savingEdit ? 'Saving…' : 'Save'}</Button>
      <Button size="sm" variant="outline" className="h-7 px-3 text-xs" disabled={savingEdit} onClick={cancelEdit}><X size={12} className="mr-1" />Cancel</Button>
    </div>
  );

  const saveEdit = async () => {
    const section = editSection;
    setSavingEdit(true);
    try {
      // 1) Descriptive, scheduling & priority corrections (not lock-gated) — Work Details only.
      if (section === 'work') await axios.put(`${API}/work-orders/${workOrderId}`, {
        job_name: draft.job_name, trade_type: draft.trade_type,
        requestor_name: draft.requestor_name,
        requestor_email: draft.requestor_email,
        requestor_phone: draft.requestor_phone,
        customer_work_order_number: draft.customer_work_order_number,
        customer_po_number: draft.customer_po_number,
        description_of_work: draft.description_of_work,
        work_performed: draft.work_performed,
        special_instructions: draft.special_instructions,
        internal_notes: draft.internal_notes,
        priority_level: draft.priority_level || null,
        date_received: draft.date_received || null,
        required_service_date: draft.required_service_date || null,
        required_completion_date: draft.required_completion_date || null,
        scheduled_date: draft.scheduled_date || null,
        service_window: draft.service_window || null,
        main_contact_name: draft.main_contact_name,
        main_contact_email: draft.main_contact_email,
        main_contact_phone: draft.main_contact_phone,
        local_contact_name: draft.local_contact_name,
        local_contact_phone: draft.local_contact_phone,
        service_location: draft.service_location,
        service_address: draft.service_address,
        service_city: draft.service_city,
        service_state: draft.service_state,
        service_zip: draft.service_zip,
      }, authHeaders);

      // 2) Client billing (single source of truth) if it changed — Billing section only.
      const origCharge = String((wo.client_charge ?? wo.service_call_amount) ?? '');
      const origAddl = String((wo.additional_charges ?? wo.additional_approved_charges) ?? '');
      if (section === 'billing' && (String(draft.client_charge) !== origCharge ||
          String(draft.additional_charges) !== origAddl ||
          draft.service_fee_type !== (wo.service_fee_type || 'pre_established') ||
          (draft.additional_charges_note || '') !== (wo.additional_charges_note || ''))) {
        await axios.post(`${API}/work-orders/${workOrderId}/costs`, {
          client_charge: draft.client_charge === '' ? null : Number(draft.client_charge),
          service_fee_type: draft.service_fee_type,
          additional_charges: draft.additional_charges === '' ? null : Number(draft.additional_charges),
          additional_charges_note: draft.additional_charges_note || null,
        }, authHeaders);
      }

      // 3) Internal job costing (labor/travel/supplies/materials/etc.) via the panel — Billing only.
      if (section === 'billing' && costingRef.current?.save) await costingRef.current.save();

      // 4) Assignment (master personnel / external provider) if it changed — Assignment section only.
      const asgChanged = section === 'assignment' && (asg.fulfillment_type !== (wo.fulfillment_type || 'Unassigned') ||
        (asg.assigned_to || '') !== (wo.assigned_to || '') ||
        (asg.assigned_personnel_id || '') !== (wo.assigned_personnel_id || '') ||
        (asg.service_provider_id || '') !== (wo.service_provider_id || '') ||
        (asg.responsible_manager_id || '') !== (wo.responsible_manager_id || '') ||
        (asg.compensation_classification || '') !== (wo.compensation_classification || ''));
      if (asgChanged) {
        const body = { fulfillment_type: asg.fulfillment_type, assigned_to: asg.fulfillment_type === 'TFS Internal Personnel' ? (asg.assigned_to || null) : null };
        if (asg.responsible_manager_id) {
          body.responsible_manager_id = asg.responsible_manager_id;
          const m = managers.find((u) => u.id === asg.responsible_manager_id);
          if (m) body.responsible_manager_name = m.name || m.full_name || m.email;
        }
        if (asg.fulfillment_type === 'TFS Subcontractor' || asg.fulfillment_type === 'Third-Party Service Provider') {
          body.service_provider_id = asg.service_provider_id || null;
        }
        if (asg.fulfillment_type === 'Assigned Location Personnel') {
          body.assigned_personnel_id = asg.assigned_personnel_id || null;
          body.compensation_classification = asg.compensation_classification || null;
          body.payout_amount = asg.payout_amount ? parseFloat(asg.payout_amount) : null;
          body.payout_basis = asg.payout_basis || null;
          body.payout_approved_by = asg.payout_approved_by || null;
          body.payroll_notes = asg.payroll_notes || null;
        }
        await axios.post(`${API}/work-orders/${workOrderId}/assignment`, body, authHeaders);
      }

      // 5) Status change (isolated — Work Details section only; its own transition
      //    validation; never dispatches/bills/notifies).
      if (section === 'work' && draft.status && draft.status !== wo.status) {
        try {
          await axios.post(`${API}/work-orders/${workOrderId}/status`,
            { status: draft.status, notes: 'Corrected via inline edit' }, authHeaders);
        } catch (se) {
          toast.error(se.response?.data?.detail || 'Details saved, but the status change was not allowed');
        }
      }

      toast.success('Work order saved');
      captureAnchor();
      setEditSection(null); setDraft({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save changes');
    } finally { setSavingEdit(false); }
  };

  // Reusable assignment editor (master personnel records) — used inline in edit mode
  // and inside the standalone Assignment dialog. Bound to `asg` / `quickAdd` state.
  const renderAssignmentEditor = () => {
    const q = asgSearch.trim().toLowerCase();
    const fu = q ? fieldUsers.filter((u) => (u.name || '').toLowerCase().includes(q)) : fieldUsers;
    const pv = q ? providers.filter((p) => `${p.company_name || ''} ${p.provider_type || ''}`.toLowerCase().includes(q)) : providers;
    const pe = q ? personnel.filter((p) => `${p.name || ''} ${p.employee_identifier || ''}`.toLowerCase().includes(q)) : personnel;
    const needsPicker = ['TFS Internal Personnel', 'TFS Subcontractor', 'Third-Party Service Provider', 'Assigned Location Personnel'].includes(asg.fulfillment_type);
    return (
    <div className="space-y-4">
      <div>
        <Label>Fulfillment Type</Label>
        <Select value={asg.fulfillment_type} onValueChange={(v) => setAsg((a) => ({ ...a, fulfillment_type: v, assigned_to: '', assigned_personnel_id: '' }))}>
          <SelectTrigger data-testid="wo-fulfillment-select"><SelectValue /></SelectTrigger>
          <SelectContent>{FULFILLMENT_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-[11px] text-slate-400 mt-1">Fulfillment type is not an assignee. A named person is required to complete the assignment.</p>
      </div>

      {needsPicker && (
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={asgSearch} onChange={(e) => setAsgSearch(e.target.value)} placeholder="Search personnel / providers by name" data-testid="wo-assignee-search" className="pl-8" />
        </div>
      )}

      {asg.fulfillment_type === 'TFS Internal Personnel' && (
        <div>
          <Label>Assign To <span className="font-normal text-slate-400">— W-2 Employee</span></Label>
          <Select value={asg.assigned_to} onValueChange={(v) => setAsg((a) => ({ ...a, assigned_to: v }))}>
            <SelectTrigger data-testid="wo-assignee-select"><SelectValue placeholder="Select personnel" /></SelectTrigger>
            <SelectContent>{fu.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · W-2 Employee</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {(asg.fulfillment_type === 'TFS Subcontractor' || asg.fulfillment_type === 'Third-Party Service Provider') && (
        <div>
          <Label>Assign Service Provider / Subcontractor * <span className="font-normal text-slate-400">— 1099 / W-9</span></Label>
          <Select value={asg.service_provider_id} onValueChange={(v) => setAsg((a) => ({ ...a, service_provider_id: v }))}>
            <SelectTrigger data-testid="wo-provider-select"><SelectValue placeholder={providers.length ? 'Select a service provider' : 'No providers on file — add one in Admin Hub'} /></SelectTrigger>
            <SelectContent>{pv.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name} · 1099{p.provider_type ? ` · ${p.provider_type}` : ''}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-[11px] text-slate-400 mt-1">Select the governed provider from Admin Hub that performed this work. An individual employee is not required for a subcontractor.</p>
        </div>
      )}

      {asg.fulfillment_type === 'Assigned Location Personnel' && (
        <>
          <div>
            <div className="flex items-center justify-between">
              <Label>Assigned Location Employee *</Label>
              <button type="button" className="text-xs font-semibold text-[#0E2A47] hover:underline" data-testid="wo-quickadd-toggle"
                onClick={() => setQuickAdd(quickAdd ? null : { name: '', employee_identifier: '', phone: '', email: '', relationship_type: 'Location Employee', has_rout_access: false })}>
                {quickAdd ? 'Cancel add' : '+ Add employee'}
              </button>
            </div>
            <Select value={asg.assigned_personnel_id} onValueChange={(v) => setAsg((a) => ({ ...a, assigned_personnel_id: v }))}>
              <SelectTrigger data-testid="wo-personnel-select"><SelectValue placeholder={personnel.length ? 'Select a location employee' : 'No employees on file — add one'} /></SelectTrigger>
              <SelectContent>{pe.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.employee_identifier ? ` · ${p.employee_identifier}` : ''}{p.has_rout_access ? ' · ROUT' : ''}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">Lists active personnel for {wo.customer_name} · {wo.service_location}.</p>
          </div>

          {quickAdd && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="wo-quickadd-form">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">New Location Employee</div>
              <Input placeholder="Full name *" value={quickAdd.name} onChange={(e) => setQuickAdd((q) => ({ ...q, name: e.target.value }))} data-testid="wo-quickadd-name" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Employee/Contractor ID" value={quickAdd.employee_identifier} onChange={(e) => setQuickAdd((q) => ({ ...q, employee_identifier: e.target.value }))} data-testid="wo-quickadd-id" />
                <Select value={quickAdd.relationship_type} onValueChange={(v) => setQuickAdd((q) => ({ ...q, relationship_type: v }))}>
                  <SelectTrigger data-testid="wo-quickadd-reltype"><SelectValue /></SelectTrigger>
                  <SelectContent>{RELATIONSHIP_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Phone" value={quickAdd.phone} onChange={(e) => setQuickAdd((q) => ({ ...q, phone: e.target.value }))} data-testid="wo-quickadd-phone" />
                <Input placeholder="Email" value={quickAdd.email} onChange={(e) => setQuickAdd((q) => ({ ...q, email: e.target.value }))} data-testid="wo-quickadd-email" />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={quickAdd.has_rout_access} onChange={(e) => setQuickAdd((q) => ({ ...q, has_rout_access: e.target.checked }))} data-testid="wo-quickadd-rout" />
                Has ROUT (Field Portal) access
              </label>
              <Button size="sm" className="bg-[#0E2A47]" disabled={busy} onClick={submitQuickAdd} data-testid="wo-quickadd-save">Save employee</Button>
            </div>
          )}

          <div>
            <Label>Compensation Classification *</Label>
            <Select value={asg.compensation_classification} onValueChange={(v) => setAsg((a) => ({ ...a, compensation_classification: v }))}>
              <SelectTrigger data-testid="wo-compensation-select"><SelectValue placeholder="Select classification" /></SelectTrigger>
              <SelectContent>{COMPENSATION_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">An above-scope client service does not automatically create an employee payout.</p>
          </div>

          {asg.compensation_classification === 'Additional Payout Required' && (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Payout amount ($)" type="number" value={asg.payout_amount} onChange={(e) => setAsg((a) => ({ ...a, payout_amount: e.target.value }))} data-testid="wo-payout-amount" />
              <Input placeholder="Calculation basis" value={asg.payout_basis} onChange={(e) => setAsg((a) => ({ ...a, payout_basis: e.target.value }))} data-testid="wo-payout-basis" />
              <Input placeholder="Approving manager" value={asg.payout_approved_by} onChange={(e) => setAsg((a) => ({ ...a, payout_approved_by: e.target.value }))} data-testid="wo-payout-approver" />
              <Input placeholder="Payroll notes" value={asg.payroll_notes} onChange={(e) => setAsg((a) => ({ ...a, payroll_notes: e.target.value }))} data-testid="wo-payroll-notes" />
            </div>
          )}
        </>
      )}

      <div className="pt-1 border-t border-slate-100">
        <Label>Responsible TFS Operations Manager <span className="font-normal text-slate-400">— accountable for the work (not the technician)</span></Label>
        <Select value={asg.responsible_manager_id || 'none'} onValueChange={(v) => setAsg((a) => ({ ...a, responsible_manager_id: v === 'none' ? '' : v }))}>
          <SelectTrigger data-testid="wo-responsible-manager-select"><SelectValue placeholder="Select a responsible operations manager" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— None —</SelectItem>
            {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-400 mt-1">Optional at assignment. Required before dispatch for internally-generated work. PEV work orders are already authorized by the customer.</p>
      </div>
    </div>
    );
  };

  if (loading) return <div className="py-16 text-center text-slate-400">Loading…</div>;
  if (!wo) return <div className="py-16 text-center text-slate-400">Work order not found.</div>;

  const money = (v) => (typeof v === 'number' ? `$${v.toFixed(2)}` : null);
  const openCosts = () => {
    setCostForm({
      client_charge: (wo.client_charge ?? wo.service_call_amount) ?? '',
      service_fee_type: wo.service_fee_type || 'pre_established',
      additional_charges: (wo.additional_charges ?? wo.additional_approved_charges) ?? '',
      additional_charges_note: wo.additional_charges_note || '',
    });
    setDialog('costs');
  };
  const saveCosts = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/${workOrderId}/costs`, {
        client_charge: costForm.client_charge === '' ? null : Number(costForm.client_charge),
        service_fee_type: costForm.service_fee_type,
        additional_charges: costForm.additional_charges === '' ? null : Number(costForm.additional_charges),
        additional_charges_note: costForm.additional_charges_note || null,
      }, authHeaders);
      toast.success('Client billing updated'); setDialog(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save billing'); }
    finally { setBusy(false); }
  };
  const openMaterials = () => {
    setMats((wo.materials || []).map((m) => ({
      description: m.description || m.item || '', category: m.category || m.material_category || '', quantity: m.quantity ?? '', unit_cost: m.unit_cost ?? '',
      purchase_date: m.purchase_date || '', vendor: m.vendor || '', notes: m.notes || '',
      receipt_photo_id: m.receipt_photo_id || null, material_photo_id: m.material_photo_id || null,
    })));
    setMatStatus(wo.materials_status || (wo.materials?.length ? 'entered' : ''));
    setDialog('materials');
  };
  const addMatRow = () => setMats((r) => [...r, { description: '', quantity: '', unit_cost: '', purchase_date: '', vendor: '', notes: '', receipt_photo_id: null, material_photo_id: null }]);
  const updMat = (i, k, v) => setMats((r) => r.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const rmMat = (i) => setMats((r) => r.filter((_, idx) => idx !== i));
  const uploadMatPhoto = async (i, k, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('kind', k);
    try {
      const r = await axios.post(`${API}/work-orders/${workOrderId}/photos`, fd, authHeaders);
      updMat(i, k === 'receipt' ? 'receipt_photo_id' : 'material_photo_id', r.data.photo_id);
      toast.success('Photo attached');
    } catch (e) { toast.error('Photo upload failed'); }
  };
  const saveMaterials = async () => {
    setBusy(true);
    try {
      const cleaned = mats.filter((m) => m.description).map((m) => ({
        description: m.description, quantity: m.quantity === '' ? null : Number(m.quantity),
        unit_cost: m.unit_cost === '' ? null : Number(m.unit_cost),
        purchase_date: m.purchase_date || null, vendor: m.vendor || null, notes: m.notes || null,
        receipt_photo_id: m.receipt_photo_id, material_photo_id: m.material_photo_id,
      }));
      await axios.post(`${API}/work-orders/${workOrderId}/materials`, {
        materials: cleaned, materials_status: matStatus === 'none_used' ? 'none_used' : (cleaned.length ? 'entered' : null),
      }, authHeaders);
      toast.success('Materials saved'); setDialog(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save materials'); }
    finally { setBusy(false); }
  };
  const matLineTotal = (m) => {
    const q = Number(m.quantity), u = Number(m.unit_cost);
    return (!isNaN(q) && !isNaN(u) && m.quantity !== '' && m.unit_cost !== '') ? q * u : null;
  };

  const hasCostData = ['service_call_amount', 'materials_total', 'additional_approved_charges', 'total_work_order_cost']
    .some((k) => typeof wo[k] === 'number') || (wo.materials || []).length > 0 || !!wo.materials_status;

  return (
    <div className="space-y-5" data-testid="work-order-detail">
      {/* Compact persistent summary banner (TFS navy) — sticky beneath the top bar */}
      <div className="relative sticky top-[63px] z-20 -mx-4 lg:-mx-8 -mt-6 border-b border-white/10 bg-[#0E2A47] px-4 lg:px-8 pt-3 pb-3 lg:pb-9 text-white shadow-md" data-testid="wo-summary-banner">
        {/* MOBILE COMPACT summary (phones) */}
        <div className="lg:hidden" data-testid="wo-banner-compact">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-mono text-xs font-bold text-[#A3D65C]" data-testid="wo-number-m">{wo.tfs_work_order_number}</span>
                {editingWork ? (
                  <select value={draft.status} onChange={(e) => dset('status', e.target.value)} data-testid="wo-edit-status-m"
                    className="max-w-[10rem] rounded-md border border-white/40 bg-white/10 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    {Array.from(new Set([draft.status, ...STATUS_OPTIONS].filter(Boolean))).map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                  </select>
                ) : (<span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusColor(wo.status)}`} data-testid="wo-status-m">{val(wo.status)}</span>)}
              </div>
              <h1 className="mt-1 text-[15px] font-semibold leading-snug text-white line-clamp-2" data-testid="wo-title-m">{shortJobTitle(wo.job_name)}</h1>
              <div className="mt-1 flex items-center gap-1.5">
                <CustomerLogoImg customerId={wo.customer_id} token={token} />
                <span className="truncate text-[13px] font-medium text-slate-100" data-testid="wo-customer-m">{val(wo.customer_name)}{wo.service_location ? ` · ${wo.service_location}` : ''}</span>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-[#0E2A47] shadow"><span className="text-base font-extrabold" data-testid="wo-priority-m">{val(wo.priority_level)}</span></div>
              <span className="mt-0.5 text-[9px] uppercase tracking-wide text-[#A3D65C]">Priority</span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 border-white/40 bg-transparent px-2.5 text-xs text-white hover:bg-white/10" onClick={() => setHistoryOpen(true)} data-testid="wo-history-btn-m"><ClipboardList size={12} className="mr-1" />History</Button>
            {canEdit && (editingWork ? (
              <>
                <Button size="sm" className="h-7 bg-[#8FC93A] px-3 text-xs font-semibold text-[#0E2A47] hover:bg-[#7fb82f]" disabled={savingEdit} onClick={saveEdit} data-testid="wo-banner-save-btn-m"><Save size={12} className="mr-1" />{savingEdit ? 'Saving…' : 'Save'}</Button>
                <Button size="sm" variant="outline" className="h-7 border-white/40 bg-transparent px-3 text-xs text-white hover:bg-white/10" disabled={savingEdit} onClick={cancelEdit} data-testid="wo-banner-cancel-btn-m"><X size={12} className="mr-1" />Cancel</Button>
              </>
            ) : !editing ? (
              <Button size="sm" variant="outline" className="h-7 border-white/40 bg-transparent px-3 text-xs text-white hover:bg-white/10" onClick={() => enterEdit('work')} data-testid="wo-banner-edit-btn-m"><Wrench size={12} className="mr-1" />Edit</Button>
            ) : null)}
          </div>
        </div>

        {/* DESKTOP FULL banner (lg+) */}
        <div className="hidden lg:block">
        <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
          <div className="min-w-0 max-w-full lg:max-w-[44%]">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold text-[#A3D65C]" data-testid="wo-number">{wo.tfs_work_order_number}</span>
              {editingWork ? (
                <select value={draft.status} onChange={(e) => dset('status', e.target.value)} data-testid="wo-edit-status"
                  className="rounded-md border border-white/40 bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white">
                  {Array.from(new Set([draft.status, ...STATUS_OPTIONS].filter(Boolean))).map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                </select>
              ) : (
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusColor(wo.status)}`} data-testid="wo-status">{val(wo.status)}</span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <CustomerLogoImg customerId={wo.customer_id} token={token} />
              <span className="text-[15px] font-bold text-white" data-testid="wo-customer">{val(wo.customer_name)}</span>
            </div>
            <div className="text-[13px] leading-tight text-slate-200">{val(wo.service_location)}{wo.area_name ? ` · ${wo.area_name}` : ''}</div>
            {wo.service_address && <div className="text-[12px] leading-tight text-slate-300">{[wo.service_address, wo.service_city, wo.service_state].filter(Boolean).join(', ')}{wo.service_zip ? ` ${wo.service_zip}` : ''}</div>}
            <h1 className="mt-1 text-base font-semibold text-white" data-testid="wo-title">{shortJobTitle(wo.job_name)}</h1>
          </div>
          <div className="min-w-0 space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[#A3D65C]">Requested By <span className="text-slate-300 normal-case">(client)</span></div>
              {editingWork ? (
                <Input value={draft.requestor_name} onChange={(e) => dset('requestor_name', e.target.value)} placeholder="Client requester name" data-testid="wo-edit-requested-by"
                  className="mt-0.5 h-6 w-full max-w-[13rem] border-white/40 bg-white/10 px-2 py-0 text-sm text-white placeholder:text-slate-400" />
              ) : (
                wo.requestor_name
                  ? <div className="text-sm font-medium text-white" data-testid="wo-requested-by">{wo.requestor_name}</div>
                  : <div className="flex items-center gap-1 text-sm font-semibold text-amber-300" data-testid="wo-requested-by-flag"><AlertTriangle size={12} /> Needs correction — press Edit</div>
              )}
            </div>
            <BannerField label="Entered By">{val(wo.entered_by_name)}</BannerField>
            {(wo.client_authorized_by || editingWork) && <BannerField label="Authorized By">{val(wo.client_authorized_by)}</BannerField>}
            <BannerField label="Service Class">{val(wo.service_classification)}</BannerField>
          </div>
          <div className="flex items-start gap-4">
            <div>
              {editingWork ? (
                <>
                  <div><div className="text-[10px] uppercase tracking-wide text-[#A3D65C]">Requested On</div>
                    <Input type="date" value={draft.date_received} onChange={(e) => dset('date_received', e.target.value)} data-testid="wo-edit-requested-on" className="mt-0.5 h-6 w-[9.5rem] border-white/40 bg-white/10 px-2 py-0 text-xs text-white" /></div>
                  <div className="mt-1.5"><div className="text-[10px] uppercase tracking-wide text-[#A3D65C]">Required By</div>
                    <Input type="date" value={draft.required_completion_date} onChange={(e) => dset('required_completion_date', e.target.value)} data-testid="wo-edit-required-by" className="mt-0.5 h-6 w-[9.5rem] border-white/40 bg-white/10 px-2 py-0 text-xs text-white" /></div>
                </>
              ) : (
                <>
                  <BannerField label="Requested On">{fmt(wo.date_received)}</BannerField>
                  <div className="mt-2"><BannerField label="Required By">{fmt(wo.required_completion_date || wo.required_service_date)}</BannerField></div>
                </>
              )}
            </div>
            <div className="flex flex-col items-center">
              {editingWork ? (
                <select value={draft.priority_level} onChange={(e) => dset('priority_level', e.target.value)} data-testid="wo-edit-priority-banner"
                  className="h-14 w-14 rounded-lg bg-white text-center text-lg font-extrabold text-[#0E2A47] shadow">
                  {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.value}</option>)}
                </select>
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white text-[#0E2A47] shadow" data-testid="wo-priority"><span className="text-2xl font-extrabold">{val(wo.priority_level)}</span></div>
              )}
              <span className="mt-1 text-[10px] uppercase tracking-wide text-[#A3D65C]">Priority</span>
            </div>
          </div>
        </div>

        {/* Edit / Save / Cancel — anchored bottom-right, out of the summary flow so the
            banner keeps the same layout and height in both view and edit modes */}
        {canEdit && (
          <div className="absolute bottom-2 right-4 lg:right-8 flex items-center gap-2" data-testid="wo-banner-edit-actions">
            {editingWork ? (
              <>
                <Button size="sm" className="h-7 bg-[#8FC93A] px-3 font-semibold text-[#0E2A47] hover:bg-[#7fb82f]" disabled={savingEdit} onClick={saveEdit} data-testid="wo-banner-save-btn"><Save size={13} className="mr-1" />{savingEdit ? 'Saving…' : 'Save'}</Button>
                <Button size="sm" variant="outline" className="h-7 border-white/50 bg-transparent px-3 text-white hover:bg-white/10" disabled={savingEdit} onClick={cancelEdit} data-testid="wo-banner-cancel-btn"><X size={13} className="mr-1" />Cancel</Button>
              </>
            ) : !editing ? (
              <Button size="sm" variant="outline" className="h-7 border-white/50 bg-transparent px-3 text-white hover:bg-white/10" onClick={() => enterEdit('work')} data-testid="wo-banner-edit-btn"><Wrench size={13} className="mr-1" />Edit</Button>
            ) : null}
          </div>
        )}
        </div>
      </div>
      {/* Mobile-only expandable details — scrolls with the document */}
      <div className="lg:hidden rounded-xl border border-slate-200 bg-white" data-testid="wo-mobile-details">
        <button onClick={() => setMDetails((o) => !o)} className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-semibold text-[#0E2A47]" data-testid="wo-mobile-details-toggle">
          <span>Details</span><ChevronDown size={16} className={`transition-transform ${mDetails ? 'rotate-180' : ''}`} />
        </button>
        {mDetails && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-slate-100 px-4 py-3 text-sm" data-testid="wo-mobile-details-body">
            <div className="col-span-2"><div className="text-[10px] uppercase tracking-wide text-slate-400">Address</div><div className="text-slate-700">{[wo.service_address, wo.service_city, wo.service_state].filter(Boolean).join(', ')}{wo.service_zip ? ` ${wo.service_zip}` : ''}{wo.area_name ? ` · ${wo.area_name}` : ''}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Requested By (client)</div><div className="text-slate-700">{val(wo.requestor_name)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Entered By</div><div className="text-slate-700">{val(wo.entered_by_name)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Service Class</div><div className="text-slate-700">{val(wo.service_classification)}</div></div>
            {wo.client_authorized_by && <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Authorized By</div><div className="text-slate-700">{wo.client_authorized_by}</div></div>}
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Requested On</div><div className="text-slate-700">{fmt(wo.date_received)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Required By</div><div className="text-slate-700">{fmt(wo.required_completion_date || wo.required_service_date)}</div></div>
          </div>
        )}
      </div>
      {/* Shortcuts, immediately beneath the banner */}
      {editing && (
        <div className="flex items-center gap-2 rounded-md border border-[#8FC93A]/50 bg-[#8FC93A]/10 px-3 py-1.5 text-xs font-medium text-[#3f5c14]" data-testid="wo-edit-hint">
          <Wrench size={13} className="shrink-0" /> Editing <b className="capitalize">{editSection}</b> — only this section is unlocked; other sections stay locked. <b>Save</b> commits just these fields and never submits billing, runs payroll, or notifies the client.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2" data-testid="wo-toolbar">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={() => setDetailsOpen(true)} data-testid="wo-details-btn"><FileText size={14} className="mr-1.5" />Details</Button>
          {canEdit && !editing && (
            <>
              {!woLocked && <Button size="sm" className="h-9 bg-[#0E2A47] text-white hover:bg-[#0E2A47]/90" onClick={openAssignment} data-testid="wo-assign-action-btn"><UserPlus size={15} className="mr-1.5" />Assign to T.E.A.M. Member</Button>}
              {!woLocked && <Button size="sm" className="h-9 bg-[#8FC93A] text-[#0E2A47] hover:bg-[#7fb82f]" onClick={openDispatch} data-testid="wo-dispatch-action-btn"><Truck size={15} className="mr-1.5" />Dispatch Work Order</Button>}
              <Button size="sm" variant="outline" className="h-9" onClick={() => { setSts({ status: wo.status || '', notes: '' }); setDialog('status'); }} data-testid="wo-change-status-btn"><ClipboardList size={14} className="mr-1.5" />Change Status</Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <button className="flex items-center gap-1.5 font-medium text-[#0E2A47] hover:underline" onClick={() => setHistoryOpen(true)} data-testid="wo-history-btn"><ClipboardList size={15} />History</button>
          {wo.service_request_id && onOpenRequest && <button className="flex items-center gap-1.5 font-medium text-[#0E2A47] hover:underline" onClick={() => onOpenRequest(wo.service_request_id)} data-testid="wo-linked-sr-btn"><Link2 size={15} />Linked Service Request</button>}
          <button className="flex items-center gap-1.5 font-medium text-[#0E2A47] hover:underline" onClick={downloadReport} data-testid="wo-report-btn"><Download size={15} />Report</button>
          {canPurge && (
            <button className="flex items-center gap-1.5 font-medium text-red-600 hover:underline" onClick={() => setPurgeOpen(true)} data-testid="wo-purge-btn"><Trash2 size={15} />Purge</button>
          )}
        </div>
      </div>
      <TagWorkPricingCard workOrderId={workOrderId} />
      {canPurge && wo && (
        <WorkOrderPurgeDialog open={purgeOpen} woIds={[wo.id]} onOpenChange={setPurgeOpen}
          onPurged={(res) => { showPurgeUndoToast(res, token); onBack?.(); }} />
      )}

      <div className="w-full">
        <div className="space-y-6 min-w-0">

          {/* ============ MISSING INFORMATION — ACTION REQUIRED ============ */}
          {canManage && !woLocked && (() => {
            const items = [];
            const assigned = wo.assignment_status === 'Assigned' && wo.assigned_to_name;
            if (!assigned) items.push({ key: 'assignee', label: 'Assigned T.E.A.M. Member required before dispatch.', onClick: openAssignment });
            if (!wo.service_classification) items.push({ key: 'svc-class', label: 'Service Classification missing.', onClick: () => enterEdit('work') });
            if (wo.data_exception) items.push({ key: 'requested-by', label: 'Requested By requires review.', onClick: () => enterEdit('work') });
            if (!wo.required_completion_date && !wo.required_service_date) items.push({ key: 'req-date', label: 'Required completion date missing.', onClick: () => enterEdit('work') });
            {
              const isPev = !!(wo.pev_inspection_number || wo.created_via === 'pev_authorization');
              const isSr = wo.source_type === 'service_request';
              if (!isPev && !isSr && !wo.responsible_manager_name) {
                items.push({ key: 'resp-mgr', label: 'Responsible TFS Operations Manager required before dispatch.', onClick: openAssignment });
              }
            }
            if (!wo.dispatched_at && !wo.scheduled_date && !wo.service_window) items.push({ key: 'sched', label: 'Scheduled date / service window required before dispatch.', onClick: openDispatch });
            if (wo.pev_renewed_authorization_required) items.push({ key: 'auth', label: 'Customer authorization requires review.', onClick: () => { try { document.querySelector('[data-testid="wo-section-pev-origin"]')?.scrollIntoView({ behavior: 'smooth' }); } catch { /* noop */ } } });
            if (!items.length) return null;
            return (
              <Card className="border-amber-300 bg-amber-50 shadow-sm rounded-2xl" data-testid="wo-missing-info">
                <CardContent className="py-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-amber-800"><AlertTriangle size={16} /> Missing Information — Action Required</div>
                  <ul className="mt-2 space-y-1">
                    {items.map((it) => (
                      <li key={it.key}>
                        <button onClick={it.onClick} data-testid={`wo-missing-${it.key}`}
                          className="flex items-center gap-1.5 text-sm font-medium text-amber-900 hover:underline">
                          <ChevronRight size={14} /> {it.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })()}

          {/* ============ ORIGIN & CUSTOMER AUTHORIZATION (PEV-sourced) ============ */}
          {wo.pev_inspection_number && (
          <Card className="border-[#8FC93A]/50 shadow-sm rounded-2xl overflow-hidden" data-testid="wo-section-pev-origin">
            <CardHeader className="bg-[#0E2A47] rounded-t-2xl px-4 py-2.5">
              <CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white flex items-center gap-2"><Link2 size={15} /> Origin &amp; Customer Authorization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {wo.pev_renewed_authorization_required && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 flex items-center gap-2" data-testid="wo-renewed-auth-banner">
                  <AlertTriangle size={14} /> Approved terms were overridden by a Global Administrator — renewed customer authorization may be required before this work proceeds.
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Originating Record</div>
                  <button type="button" onClick={() => { try { window.open(`/tfs-ops?pev=${encodeURIComponent(wo.pev_inspection_number)}`, '_blank'); } catch { /* noop */ } }}
                    className="flex items-center gap-1.5 text-sm font-bold text-[#0E2A47] hover:underline" data-testid="wo-pev-source-link">
                    <FileText size={14} /> Property Evaluation {wo.pev_inspection_number} <ChevronRight size={13} />
                  </button>
                </div>
                {wo.pev_finding_number != null && (
                  <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Finding</div>
                    <div className="text-sm font-medium text-slate-700" data-testid="wo-pev-finding">#{wo.pev_finding_number} — {wo.job_name}</div></div>
                )}
                {wo.authorized_at && (
                  <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Authorized</div>
                    <div className="text-sm font-medium text-slate-700" data-testid="wo-pev-authorized-at">{fmt(wo.authorized_at, true)}</div></div>
                )}
                {wo.customer_authorization_ref && (
                  <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Authorization Ref</div>
                    <div className="text-xs font-mono text-slate-500" data-testid="wo-pev-auth-ref">{String(wo.customer_authorization_ref).slice(0, 12)}…</div></div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Requested By</div>
                  <div className="text-sm font-semibold text-[#0E2A47]" data-testid="wo-identity-requested">{val(wo.requester_contact_name)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Entered Into ROUT By</div>
                  <div className="text-sm font-semibold text-[#0E2A47]" data-testid="wo-identity-entered">{val(wo.entered_by_name)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Authorized By</div>
                  <div className="text-sm font-semibold text-[#0E2A47]" data-testid="wo-identity-authorized">{val(wo.client_authorized_by)}</div>
                </div>
              </div>

              {wo.pev_pricing_locked && (
                <div className="rounded-xl border border-[#8FC93A]/40 bg-[#8FC93A]/10 p-4" data-testid="wo-approved-pricing">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#3f6212] flex items-center gap-1.5"><BadgeCheck size={14} /> Customer-Approved Pricing (Locked)</div>
                    {isOwner && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOvr({ new_amount: '', new_scope: '', reason: '' })} data-testid="wo-pricing-override-btn">Global-Admin override</Button>}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm max-w-md">
                    <span className="text-slate-600">Approved Fixed Price (Client Charge)</span>
                    <span className="text-right font-bold text-[#0E2A47]" data-testid="wo-approved-fixed-price">{money(wo.customer_billing_amount ?? wo.pev_approved_fixed_price) || '$0.00'}</span>
                    {wo.pev_customer_nte != null && (<>
                      <span className="text-slate-600">Customer NTE (spending ceiling)</span>
                      <span className="text-right font-semibold text-amber-700" data-testid="wo-customer-nte">{money(wo.pev_customer_nte)}</span>
                    </>)}
                    {wo.customer_billing_amount != null && wo.pev_approved_fixed_price != null && wo.customer_billing_amount !== wo.pev_approved_fixed_price && (<>
                      <span className="text-slate-400 text-xs">Originally approved</span>
                      <span className="text-right text-xs text-slate-500 line-through" data-testid="wo-original-approved">{money(wo.pev_approved_fixed_price)}</span>
                    </>)}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">Approved commercial terms are locked. Only a Global Administrator may override them, with a recorded reason and audit entry; the original customer-approved terms are never erased.</p>
                  {(wo.pev_pricing_override_history || []).length > 0 && (
                    <div className="mt-2 border-t border-[#8FC93A]/30 pt-2 space-y-1" data-testid="wo-override-history">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">Override history</div>
                      {(wo.pev_pricing_override_history || []).map((h, i) => (
                        <div key={i} className="text-[11px] text-slate-600">{fmt(h.at, true)} · {h.by_name}: {money(h.old_amount)} → {money(h.new_amount)} — {h.reason}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* ============ 1 · WORK DETAILS ============ */}
          <Card className="border-slate-200 shadow-sm rounded-2xl" data-testid="wo-section-work-details">
            <CardHeader className="bg-[#0E2A47] rounded-t-2xl px-4 py-2.5"><CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white">1 · Work Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Job identity, requester, priority, service class and dates live in the banner —
                  shown here once are the remaining work facts. */}
              {editingWork ? (
                <div className="space-y-3" data-testid="wo-edit-workdetails">
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    <div><Label className="text-xs">Job Name</Label><Input value={draft.job_name} onChange={(e) => dset('job_name', e.target.value)} data-testid="edit-job-name" /></div>
                    <div><Label className="text-xs">Priority</Label>
                      <Select value={draft.priority_level} onValueChange={(v) => dset('priority_level', v)}>
                        <SelectTrigger data-testid="edit-priority"><SelectValue placeholder="Select priority" /></SelectTrigger>
                        <SelectContent>{PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Trade Type</Label><Input value={draft.trade_type} onChange={(e) => dset('trade_type', e.target.value)} data-testid="edit-trade-type" /></div>
                    <div><Label className="text-xs">Customer Reference / WO #</Label><Input value={draft.customer_work_order_number} onChange={(e) => dset('customer_work_order_number', e.target.value)} data-testid="edit-customer-ref" /></div>
                    <div><Label className="text-xs">Customer PO #</Label><Input value={draft.customer_po_number} onChange={(e) => dset('customer_po_number', e.target.value)} data-testid="edit-customer-po" /></div>
                    <div><Label className="text-xs">Date Received</Label><Input type="date" value={draft.date_received} onChange={(e) => dset('date_received', e.target.value)} data-testid="edit-date-received" /></div>
                    <div><Label className="text-xs">Required Completion Date</Label><Input type="date" value={draft.required_completion_date} onChange={(e) => dset('required_completion_date', e.target.value)} data-testid="edit-required-date" /></div>
                    <div><Label className="text-xs">Required Service Date</Label><Input type="date" value={draft.required_service_date} onChange={(e) => dset('required_service_date', e.target.value)} data-testid="edit-required-service-date" /></div>
                    <div><Label className="text-xs">Scheduled Date</Label><Input type="date" value={draft.scheduled_date} onChange={(e) => dset('scheduled_date', e.target.value)} data-testid="edit-scheduled-date" /></div>
                    <div><Label className="text-xs">Service Window</Label><Input value={draft.service_window} onChange={(e) => dset('service_window', e.target.value)} placeholder="e.g., 9–11 AM" data-testid="edit-service-window" /></div>
                  </div>
                  <div><Label className="text-xs">Work Requested</Label><Textarea rows={3} value={draft.description_of_work} onChange={(e) => dset('description_of_work', e.target.value)} data-testid="edit-description" /><p className="mt-1 text-[11px] text-slate-400">The client's email, comments, or supplied instructions — preserved as the original request, not as completed work.</p></div>
                  <div><Label className="text-xs">Work Performed</Label><Textarea rows={3} value={draft.work_performed} onChange={(e) => dset('work_performed', e.target.value)} data-testid="edit-work-performed" placeholder="Describe the work TFS actually completed" /><p className="mt-1 text-[11px] text-slate-400">TFS's description of the work completed. Used for the completion report and customer email. Required before sending to the customer.</p></div>
                  <div><Label className="text-xs">Special Instructions</Label><Textarea rows={2} value={draft.special_instructions} onChange={(e) => dset('special_instructions', e.target.value)} data-testid="edit-special-instructions" /></div>
                  <div><Label className="text-xs">Internal Notes (admin-only)</Label><Textarea rows={2} value={draft.internal_notes} onChange={(e) => dset('internal_notes', e.target.value)} data-testid="edit-internal-notes" /></div>
                  <div className="border-t border-gray-100 pt-3">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#0E2A47] mb-2">Requested By <span className="font-normal normal-case text-slate-400">— the client person who requested the work (not who entered it)</span></div>
                    <div className="grid sm:grid-cols-3 gap-x-6 gap-y-3">
                      <div><Label className="text-xs">Requester Name</Label><Input value={draft.requestor_name} onChange={(e) => dset('requestor_name', e.target.value)} data-testid="edit-requestor-name" /></div>
                      <div><Label className="text-xs">Requester Email</Label><Input value={draft.requestor_email} onChange={(e) => dset('requestor_email', e.target.value)} data-testid="edit-requestor-email" /></div>
                      <div><Label className="text-xs">Requester Phone</Label><Input value={draft.requestor_phone} onChange={(e) => dset('requestor_phone', e.target.value)} data-testid="edit-requestor-phone" /></div>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">Entered by <b>{val(wo.entered_by_name)}</b>{wo.client_authorized_by ? <> · Authorized by <b>{wo.client_authorized_by}</b></> : null} (attribution preserved in audit history).</p>
                  </div>
                  <div className="border-t border-gray-100 pt-3">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#0E2A47] mb-2">Contacts</div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      <div><Label className="text-xs">Site Contact Name</Label><Input value={draft.main_contact_name} onChange={(e) => dset('main_contact_name', e.target.value)} data-testid="edit-contact-name" /></div>
                      <div><Label className="text-xs">Site Contact Phone</Label><Input value={draft.main_contact_phone} onChange={(e) => dset('main_contact_phone', e.target.value)} data-testid="edit-contact-phone" /></div>
                      <div><Label className="text-xs">Site Contact Email</Label><Input value={draft.main_contact_email} onChange={(e) => dset('main_contact_email', e.target.value)} data-testid="edit-contact-email" /></div>
                      <div><Label className="text-xs">Local Contact Name</Label><Input value={draft.local_contact_name} onChange={(e) => dset('local_contact_name', e.target.value)} data-testid="edit-local-contact-name" /></div>
                      <div><Label className="text-xs">Local Contact Phone</Label><Input value={draft.local_contact_phone} onChange={(e) => dset('local_contact_phone', e.target.value)} data-testid="edit-local-contact-phone" /></div>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-3">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#0E2A47] mb-2">Service Location</div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      <div><Label className="text-xs">Location Name</Label><Input value={draft.service_location} onChange={(e) => dset('service_location', e.target.value)} data-testid="edit-service-location" /></div>
                      <div><Label className="text-xs">Address</Label><Input value={draft.service_address} onChange={(e) => dset('service_address', e.target.value)} data-testid="edit-service-address" /></div>
                      <div><Label className="text-xs">City</Label><Input value={draft.service_city} onChange={(e) => dset('service_city', e.target.value)} data-testid="edit-service-city" /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">State</Label><Input value={draft.service_state} onChange={(e) => dset('service_state', e.target.value)} data-testid="edit-service-state" /></div>
                        <div><Label className="text-xs">ZIP</Label><Input value={draft.service_zip} onChange={(e) => dset('service_zip', e.target.value)} data-testid="edit-service-zip" /></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    <Field icon={ClipboardList} label="Trade Type">{val(wo.trade_type)}</Field>
                    <Field icon={FileText} label="Customer Reference">{val(wo.customer_reference || wo.customer_work_order_number)}</Field>
                    {wo.original_request_date && <Field icon={Calendar} label="Original Request Date">{fmt(wo.original_request_date)}</Field>}
                  </div>
                  <div>
                    <div className="text-slate-400 text-xs">Work Requested</div>
                    <p className="text-sm sm:text-[15px] text-slate-700 whitespace-pre-wrap" data-testid="wo-description">{val(wo.description_of_work || wo.special_instructions || wo.description)}</p>
                  </div>
                  {wo.special_instructions && wo.special_instructions !== wo.description_of_work && (
                    <div data-testid="wo-special-instructions-read">
                      <div className="text-slate-400 text-xs">Special Instructions</div>
                      <p className="text-sm sm:text-[15px] text-slate-700 whitespace-pre-wrap">{wo.special_instructions}</p>
                    </div>
                  )}
                  <div>
                    <div className="text-slate-400 text-xs">Work Performed</div>
                    {(wo.work_performed || wo.description_of_work_performed) ? (
                      <p className="text-sm sm:text-[15px] text-slate-700 whitespace-pre-wrap" data-testid="wo-work-performed">{wo.work_performed || wo.description_of_work_performed}</p>
                    ) : (
                      <p className="text-sm font-medium text-amber-700" data-testid="wo-work-performed-missing">Completion notes not yet entered.</p>
                    )}
                  </div>
                </>
              )}
              {/* Secondary contact + authorization details — available on demand */}
              {!editingWork && (
              <div className="border-t border-gray-100 pt-3">
                <button onClick={() => setChainOpen((o) => !o)} data-testid="wo-toggle-contacts"
                  className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#0E2A47] hover:underline">
                  {chainOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Contacts &amp; Authorization
                </button>
                {chainOpen && (
                  <div className="mt-3 space-y-3">
                    {(wo.requestor_email || wo.requestor_phone) && (
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        <Field icon={User} label="Requestor Email">{val(wo.requestor_email)}</Field>
                        <Field icon={User} label="Requestor Phone">{val(wo.requestor_phone)}</Field>
                      </div>
                    )}
                    <RequestChainCard record={wo} variant="panel" prefix="wo" />
                  </div>
                )}
              </div>
              )}
              {/* Assignment (internal-only — hidden from client views) */}
              {canManage && (
                <div className="pt-4 border-t border-gray-100" data-testid="wo-assignment-block">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Assignment{editingAssignment ? ' — editing' : ''}</div>
                    {canEdit && !editing && <Button size="sm" variant="outline" className="h-7" onClick={() => enterEdit('assignment')} data-testid="wo-edit-assignment-btn"><User size={13} className="mr-1.5" />Edit Assignment</Button>}
                    {canEdit && editingAssignment && <SectionEditActions testid="wo-assignment-edit-actions" />}
                  </div>
                  {editingAssignment ? renderAssignmentEditor() : (<>
                  {wo.fulfillment_type && wo.fulfillment_type !== 'Unassigned' && !wo.assigned_to_name && (
                    <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800" data-testid="wo-assignment-incomplete">
                      <span>{(wo.fulfillment_type === 'TFS Subcontractor' || wo.fulfillment_type === 'Third-Party Service Provider')
                        ? 'Select the service provider that performed this work.'
                        : 'Needs personnel correction — no individual is named yet. Choose the assigned person to complete this record.'}</span>
                      {canEdit && <Button size="sm" className="h-7 shrink-0 bg-[#0E2A47]" onClick={() => enterEdit('assignment')} data-testid="wo-select-personnel-btn"><User size={13} className="mr-1.5" />{(wo.fulfillment_type === 'TFS Subcontractor' || wo.fulfillment_type === 'Third-Party Service Provider') ? 'Select Provider' : 'Select Personnel'}</Button>}
                    </div>
                  )}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field icon={ClipboardList} label="Assignment Type">{val(wo.fulfillment_type === 'Assigned Location Personnel' ? 'Location Employee' : wo.fulfillment_type)}</Field>
                    <Field icon={User} label={(wo.fulfillment_type === 'TFS Subcontractor' || wo.fulfillment_type === 'Third-Party Service Provider') ? 'Service Provider' : 'Assigned to'}>
                      {wo.assigned_to_name || '—'}
                      {wo.service_provider_qsp && <span className="ml-2 rounded bg-[#0E2A47] px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="wo-provider-qsp">{wo.service_provider_qsp}</span>}
                    </Field>
                    {wo.work_performed_by && wo.work_performed_by !== wo.assigned_to_name && <Field icon={User} label="Performed by">{wo.work_performed_by}</Field>}
                    <Field icon={Flag} label="Assignment Status">{val(wo.assignment_status)}</Field>
                    <Field icon={User} label="Assigned By">{val(wo.assigned_by_name || (wo.assigned_by ? 'TFS Operations' : null))}</Field>
                    <Field icon={User} label="Responsible Ops Manager"><span data-testid="wo-responsible-manager">{val(wo.responsible_manager_name)}</span></Field>
                    <Field icon={Calendar} label="Assignment Date/Time">{fmt(wo.assignment_datetime || wo.assigned_at, true)}</Field>
                    {wo.fulfillment_type === 'Assigned Location Personnel' && (
                      <Field icon={Flag} label="Compensation">{val(wo.compensation_classification)}</Field>
                    )}
                  </div>
                  {wo.compensation_classification === 'Additional Payout Required' && (
                    <div className="grid sm:grid-cols-3 gap-4 pt-2 mt-2 border-t border-slate-100">
                      <Field icon={Flag} label="Payout Amount / Basis">{val(wo.payout_amount ? `$${wo.payout_amount}` : wo.payout_basis)}</Field>
                      <Field icon={User} label="Approved By">{val(wo.payout_approved_by)}</Field>
                      <Field icon={FileText} label="Payroll Notes">{val(wo.payroll_notes)}</Field>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-3 gap-4 pt-2 mt-2 border-t border-slate-100">
                    <Field icon={Truck} label="Dispatch Status">{wo.dispatched_at ? `${wo.dispatch_method || 'Dispatched'} · ${fmt(wo.dispatched_at, true)}` : 'Not dispatched'}</Field>
                    <Field icon={Link2} label="Request Source"><span data-testid="wo-request-source">{val(wo.request_source || (wo.source_type === 'service_request' ? 'Client-Generated Service Request' : null))}</span></Field>
                    <Field icon={ClipboardList} label="Client Source Category">{val(wo.client_source_category)}</Field>
                    <Field icon={ClipboardList} label="Service Classification">{val(wo.service_classification)}</Field>
                  </div>
                  {(wo.assignment_history || []).length > 0 && (
                    <div className="pt-2 mt-2 border-t border-slate-100" data-testid="wo-assignment-history">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Assignment History</div>
                      <div className="space-y-1">
                        {(wo.assignment_history || []).slice().reverse().map((h, i) => (
                          <div key={i} className="text-[11px] text-slate-600">
                            {fmt(h.at, true)} · <b className="text-[#0E2A47]">{h.assignment_status === 'Assigned' ? (h.assigned_to_name || '—') : 'Unassigned'}</b>
                            {h.fulfillment_type ? ` (${h.fulfillment_type})` : ''} · by {h.by_name || 'TFS Operations'}
                            {h.prior_assignee && h.prior_assignee !== h.assigned_to_name ? ` — reassigned from ${h.prior_assignee}` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  </>)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ============ 2 · PHOTOS & DOCUMENTS ============ */}
          <Card className="border-slate-200 shadow-sm rounded-2xl" data-testid="wo-section-photos-documents">
            <CardHeader className="bg-[#0E2A47] rounded-t-2xl px-4 py-2.5"><CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white flex items-center gap-2"><Camera size={15} /> 2 · Photos &amp; Documents</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {canEdit && <p className="text-xs text-slate-400">Administrators may upload or correct documentation on behalf of the assigned personnel.</p>}
              {['before', 'after', 'supporting'].map((cat) => (
                <div key={cat}>
                  <div className="flex items-center justify-between">
                    <div className="text-slate-500 text-xs font-semibold uppercase tracking-wide">{cat === 'before' ? 'Before-Service Photos' : cat === 'after' ? 'After-Service Photos' : 'Supporting Photos'}</div>
                    {canEdit && (
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47] hover:underline cursor-pointer" data-testid={`wo-choose-${cat}`}>
                          <Upload size={13} /> {uploading === cat ? 'Uploading…' : 'Choose Photos'}
                          <input type="file" accept="image/*" multiple className="hidden"
                            onChange={(e) => { uploadMedia(e.target.files, cat); e.target.value = ''; }} />
                        </label>
                        <label className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47] hover:underline cursor-pointer" data-testid={`wo-camera-${cat}`}>
                          <Camera size={13} /> Take Photo
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => { uploadMedia(e.target.files, cat); e.target.value = ''; }} />
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {mediaByCat(cat).map((m) => (
                      <div key={m.id} className="relative group">
                        <AuthImage src={`${API}/work-orders/media/${m.id}`} token={token} alt={m.caption || cat} caption={m.caption || ''} className="h-20 w-20 rounded border" testId={`wo-media-${cat}`} />
                        {canEdit && <button onClick={() => deleteMedia(m.id)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border shadow p-0.5 opacity-0 group-hover:opacity-100 z-10" data-testid="wo-media-remove"><XCircle size={13} className="text-red-500" /></button>}
                      </div>
                    ))}
                    {mediaByCat(cat).length === 0 && <span className="text-xs text-slate-300">None</span>}
                  </div>
                </div>
              ))}
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Supporting Documents</div>
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <select value={woDocCat} onChange={(e) => setWoDocCat(e.target.value)} className="text-xs border border-slate-200 rounded px-1.5 py-1" data-testid="wo-doc-category">
                        {['Receipt', 'Purchase Document', 'Permit', 'Sign-Off', 'Vendor Document', 'Other Supporting Document'].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <label className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47] hover:underline cursor-pointer" data-testid="wo-upload-document">
                        <Upload size={13} /> {uploading === 'document' ? 'Uploading…' : 'Upload'}
                        <input type="file" className="hidden" onChange={(e) => { uploadMedia(e.target.files, 'document', woDocCat); e.target.value = ''; }} />
                      </label>
                    </div>
                  )}
                </div>
                <div className="mt-1.5 space-y-1">
                  {mediaByCat('document').map((m) => (
                    <div key={m.id} className="flex items-center gap-2 text-sm" data-testid="wo-media-document">
                      <a href={`${API}/work-orders/media/${m.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0E2A47] hover:underline"><Receipt size={14} /> {m.filename}{m.doc_category ? ` · ${m.doc_category}` : ''}</a>
                      {canEdit && <button onClick={() => deleteMedia(m.id)} data-testid="wo-media-doc-remove"><XCircle size={13} className="text-red-400 hover:text-red-600" /></button>}
                    </div>
                  ))}
                  {mediaByCat('document').length === 0 && <span className="text-xs text-slate-300">None</span>}
                </div>
              </div>
              {/* Field-captured photos + on-site representative signature */}
              {(wo.before_photos || []).length > 0 && (
                <div className="border-t pt-3"><div className="text-slate-400 text-xs mb-1">Field Before Photos</div>
                  <div className="flex flex-wrap gap-2" data-testid="wo-before-photos">
                    {wo.before_photos.map((id) => <img key={id} src={String(id).startsWith('http') ? id : `${API}/work-orders/photos/${id}`} alt="before" className="h-20 w-20 rounded object-cover border" />)}
                  </div>
                </div>
              )}
              {(wo.after_photos || []).length > 0 && (
                <div><div className="text-slate-400 text-xs mb-1">Field After Photos</div>
                  <div className="flex flex-wrap gap-2" data-testid="wo-after-photos">
                    {wo.after_photos.map((id) => <img key={id} src={String(id).startsWith('http') ? id : `${API}/work-orders/photos/${id}`} alt="after" className="h-20 w-20 rounded object-cover border" />)}
                  </div>
                </div>
              )}
              {(wo.rep_signature || wo.rep_name || wo.no_rep_available) && (
                <div className="border-t pt-3"><div className="text-slate-400 text-xs mb-1">On-site Representative</div>
                  {wo.no_rep_available ? <div className="text-sm text-slate-600">No representative available</div> : (
                    <div className="text-sm text-slate-700">{wo.rep_name}{wo.rep_title ? ` · ${wo.rep_title}` : ''}
                      {wo.rep_signature && <img src={wo.rep_signature} alt="signature" data-testid="wo-signature" className="mt-2 h-16 rounded border bg-white" />}
                    </div>
                  )}
                </div>
              )}
              {wo.completion_report_superseded && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="wo-report-superseded">
                  New documentation was added after the Completion Report was generated. Regenerate the report before client delivery (the prior version is superseded).
                </div>
              )}
            </CardContent>
          </Card>

          {/* ============ 3 · BILLING ============ */}
          {canManage && (
          <Card className="border-slate-200 shadow-sm rounded-2xl overflow-hidden" data-testid="wo-section-billing">
            <CardHeader className="bg-[#5a8a1e] rounded-t-2xl px-4 py-2.5"><CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white flex items-center gap-2"><DollarSign size={15} /> 3 · Billing</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {/* One authoritative client total. Preset pricing may populate Client Charge; an
                  existing one-time charge (e.g. the $25 service fee) is read from the saved
                  billing line items and never re-entered or repriced. */}
              <div data-testid="wo-client-billing">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Client Billing{editingBilling ? ' — editing' : ''}</div>
                  {canEdit && !editing && !wo.pev_pricing_locked && <Button variant="outline" size="sm" className="h-7" onClick={() => enterEdit('billing')} data-testid="wo-edit-costs-btn">Edit Charges</Button>}
                  {canEdit && !editing && wo.pev_pricing_locked && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#3f6212]" data-testid="wo-billing-locked"><Lock size={12} /> Pricing locked — Global-Admin override only</span>}
                  {canEdit && editingBilling && <SectionEditActions testid="wo-billing-edit-actions" />}
                </div>
                {editingBilling ? (
                  <div className="rounded-xl bg-[#8FC93A]/10 border border-[#8FC93A]/30 p-4 space-y-3 max-w-md" data-testid="wo-billing-editor">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Client Charge ($)</Label><Input type="number" step="0.01" min="0" value={draft.client_charge} onChange={(e) => dset('client_charge', e.target.value)} data-testid="edit-client-charge" /></div>
                      <div><Label className="text-xs">Charge Type</Label>
                        <Select value={draft.service_fee_type} onValueChange={(v) => dset('service_fee_type', v)}>
                          <SelectTrigger data-testid="edit-fee-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pre_established">Preset</SelectItem>
                            <SelectItem value="one_time">One-time</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Additional Charges ($)</Label><Input type="number" step="0.01" min="0" value={draft.additional_charges} onChange={(e) => dset('additional_charges', e.target.value)} data-testid="edit-additional-charges" /></div>
                      <div><Label className="text-xs">Additional Note</Label><Input value={draft.additional_charges_note} onChange={(e) => dset('additional_charges_note', e.target.value)} data-testid="edit-additional-note" /></div>
                    </div>
                    <div className="flex items-center justify-between border-t border-[#8FC93A]/40 pt-2 text-sm">
                      <span className="font-bold text-[#0E2A47]">Total Client Charge</span>
                      <span className="font-extrabold text-[#0E2A47]" data-testid="wo-edit-total-client-charge">{money((Number(draft.client_charge) || 0) + (Number(draft.additional_charges) || 0)) || '$0.00'}</span>
                    </div>
                    <p className="text-[11px] text-slate-400">Changing the client charge live-recalculates overhead, profit and the minimum recommended charge in Internal Job Costing below.</p>
                  </div>
                ) : (
                <div className="rounded-xl bg-[#8FC93A]/10 border border-[#8FC93A]/30 p-4 grid grid-cols-2 gap-y-2 gap-x-6 text-sm max-w-md">
                  {(() => {
                    const liTotal = (wo.billing_line_items || []).reduce((s, li) => s + Number(li.amount || 0), 0);
                    const clientCharge = Number(wo.client_charge ?? wo.service_call_amount ?? wo.customer_billing_amount ?? 0) || liTotal;
                    const addl = Number(wo.additional_charges ?? wo.additional_approved_charges ?? 0);
                    return (
                      <>
                        <span className="text-slate-600">Client Charge{wo.service_fee_type === 'one_time' ? ' (one-time)' : (wo.service_fee_type === 'pre_established' ? ' (preset)' : '')}</span>
                        <span className="text-right font-medium text-[#0E2A47]" data-testid="wo-client-charge">{money(clientCharge) || '$0.00'}</span>
                        {addl ? (
                          <>
                            <span className="text-slate-600">Additional Charges</span>
                            <span className="text-right font-medium text-[#0E2A47]" data-testid="wo-additional-charges">{money(addl)}</span>
                            {wo.additional_charges_note && <><span className="text-slate-400 text-xs">Note</span><span className="text-right text-xs text-slate-500">{wo.additional_charges_note}</span></>}
                          </>
                        ) : null}
                        <span className="text-[#0E2A47] font-bold border-t border-[#8FC93A]/40 pt-2">Total Client Charge</span>
                        <span className="text-right text-[#0E2A47] font-extrabold border-t border-[#8FC93A]/40 pt-2" data-testid="wo-total-client-charge">{money(clientCharge + addl) || '$0.00'}</span>
                      </>
                    );
                  })()}
                </div>
                )}
              </div>

              {/* Materials & Supplies — field documentation (receipts / photos) */}
              <div className="border-t pt-4" data-testid="wo-cost-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Materials &amp; Supplies — Field Documentation</div>
                  {canEdit && <Button variant="outline" size="sm" className="h-7" onClick={openMaterials} data-testid="wo-edit-materials-btn">Materials</Button>}
                </div>
                {(wo.materials || []).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="wo-materials-table">
                      <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b">
                        <th className="py-2 pr-3">Description</th><th className="py-2 px-3 text-right">Qty</th>
                        <th className="py-2 px-3 text-right">Unit</th><th className="py-2 px-3 text-right">Total</th>
                        <th className="py-2 px-3">Vendor</th><th className="py-2 pl-3">Docs</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {wo.materials.map((m, i) => (
                          <tr key={i}>
                            <td className="py-2 pr-3 text-slate-700">{m.description || m.item || '—'}{m.notes ? <span className="block text-xs text-slate-400">{m.notes}</span> : null}{m.purchase_date ? <span className="block text-xs text-slate-400">Purchased {m.purchase_date}</span> : null}</td>
                            <td className="py-2 px-3 text-right">{m.quantity ?? '—'}</td>
                            <td className="py-2 px-3 text-right">{money(m.unit_cost) || '—'}</td>
                            <td className="py-2 px-3 text-right font-medium text-[#0E2A47]">{money(m.total_cost) || '—'}</td>
                            <td className="py-2 px-3 text-slate-600">{m.vendor || '—'}</td>
                            <td className="py-2 pl-3">
                              <div className="flex gap-1">
                                {m.receipt_photo_id && <a href={`${API}/work-orders/photos/${m.receipt_photo_id}`} target="_blank" rel="noreferrer"><img src={`${API}/work-orders/photos/${m.receipt_photo_id}`} alt="receipt" className="h-9 w-9 rounded object-cover border" data-testid={`wo-receipt-${i}`} /></a>}
                                {m.material_photo_id && <a href={`${API}/work-orders/photos/${m.material_photo_id}`} target="_blank" rel="noreferrer"><img src={`${API}/work-orders/photos/${m.material_photo_id}`} alt="material" className="h-9 w-9 rounded object-cover border" /></a>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : wo.materials_status === 'none_used' ? (
                  <div className="text-sm text-slate-500" data-testid="wo-materials-none">No materials used for this work order.</div>
                ) : (
                  <div className="text-sm text-slate-400" data-testid="wo-materials-not-entered">Materials documentation not entered.</div>
                )}
              </div>

              {/* Internal Job Costing (internal-only — labor, travel, overhead, profit) */}
              <div className="border-t pt-4">
                <CostingPanel ref={costingRef} workOrderId={workOrderId} canEdit={canEdit} refreshKey={wo.updated_at}
                  editing={editingBilling}
                  liveClientCharge={editingBilling ? ((Number(draft.client_charge) || 0) + (Number(draft.additional_charges) || 0)) : undefined} />
              </div>

              {/* Billing disposition (Global / General Administration) */}
              {canEdit && (wo.billing_status && wo.billing_status !== 'Not Submitted') && (
                <div className="border-t pt-3">
                  <div className="text-slate-500 text-xs font-semibold uppercase tracking-wide mb-1">Billing Disposition</div>
                  {wo.billing_disposition ? (
                    <div className="text-sm" data-testid="wo-billing-disposition">
                      <span className="font-semibold text-[#0E2A47]">{wo.billing_disposition}</span>
                      {wo.billing_pricing_hold && <span className="ml-2 rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[11px] font-semibold">Held for pricing</span>}
                      {wo.billing_disposition_reason && <div className="text-xs text-slate-400">{wo.billing_disposition_reason}</div>}
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => { setDispo({ disposition: '', reason: '' }); setDialog('billing-disposition'); }} data-testid="wo-set-disposition-btn">Set Billing Disposition</Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* ============ 4 · PAYROLL ============ */}
          {canManage && wo.status === 'Completed' && (
            <Card className="border-slate-200 shadow-sm rounded-2xl" data-testid="wo-section-payroll">
              <CardHeader className="bg-[#0E2A47] rounded-t-2xl px-4 py-2.5"><CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white flex items-center gap-2"><Briefcase size={15} /> 4 · Payroll</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {wo.payroll_handoff_status === 'Submitted to Payroll' ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="wo-payroll-submitted">
                    <div className="flex items-center gap-1.5 font-semibold"><CheckCircle2 size={15} /> Submitted to Payroll</div>
                    <div className="text-xs text-emerald-700">{(wo.payroll_wo_item_ids || []).length} worker pay item(s) created{wo.ready_for_payroll_by_name ? ` by ${wo.ready_for_payroll_by_name}` : ''}. Manage them in Payroll → Work-Order Pay.</div>
                  </div>
                ) : !canEdit ? (
                  <div className="text-sm text-slate-500" data-testid="wo-payroll-readonly">This work order has not yet been handed to Payroll. Payroll handoff is available to Global and General Administrators.</div>
                ) : (
                  <>
                    {wo.payroll_handoff_blocked_reason && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="wo-payroll-blocked">
                        <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={13} /> Correction required</div>
                        {wo.payroll_handoff_blocked_reason}
                      </div>
                    )}
                    {!payrollPrev ? (
                      <div>
                        <p className="text-sm text-slate-500">Hand this completed work order to Payroll. The work order number, customer, account, service location, assigned workers, completion date and PO/reference carry over automatically — you only confirm hours, cost classification, payout and timing.</p>
                        <Button size="sm" className="mt-2 bg-[#0E2A47]" onClick={loadPayrollPreview} disabled={payrollBusy} data-testid="wo-payroll-prepare-btn">
                          <DollarSign size={14} className="mr-1.5" /> Prepare Payroll Handoff
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="wo-payroll-form">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="wo-payroll-carryover">
                          <div><span className="text-slate-400 block">Work Order</span>{payrollPrev.tfs_work_order_number}</div>
                          <div><span className="text-slate-400 block">Customer · Account</span>{payrollPrev.customer_name} · {payrollPrev.account_number || '—'}</div>
                          <div><span className="text-slate-400 block">Service Location</span>{payrollPrev.service_location}</div>
                          <div><span className="text-slate-400 block">Completed</span>{fmtDate(payrollPrev.completion_date)}</div>
                          <div><span className="text-slate-400 block">Client PO / Reference</span>{payrollPrev.client_po_number || '—'} / {payrollPrev.client_reference || '—'}</div>
                        </div>
                        {payrollPrev.missing?.length > 0 && (
                          <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="wo-payroll-missing">Missing ROUT data: {payrollPrev.missing.join(', ')}. Correct the work order before handing off.</div>
                        )}
                        {(payrollPrev.workers || []).map((w) => {
                          const f = payrollForm[w.worker_key] || {};
                          const isAdd = f.cost_classification === 'additional_work_order_pay';
                          return (
                            <div key={w.worker_key} className="rounded-lg border border-slate-200 p-3" data-testid={`wo-payroll-worker-${w.worker_key}`}>
                              <div className="text-sm font-semibold text-[#0E2A47]">{w.name || '—'} <span className="text-xs font-normal text-slate-400">· {w.role || w.worker_type}</span></div>
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                <label className="flex items-center gap-1">Actual hours
                                  <input type="number" step="any" min="0" className="w-20 rounded border border-slate-300 px-2 py-1" data-testid={`wo-payroll-hours-${w.worker_key}`}
                                    value={f.actual_hours} onChange={(e) => setPf(w.worker_key, { actual_hours: e.target.value })} /></label>
                                <select className="rounded border border-slate-300 px-2 py-1" data-testid={`wo-payroll-class-${w.worker_key}`}
                                  value={f.cost_classification} onChange={(e) => setPf(w.worker_key, { cost_classification: e.target.value })}>
                                  <option value="additional_work_order_pay">Requires additional work-order pay</option>
                                  <option value="regular_compensation">Included in regular compensation</option>
                                </select>
                              </div>
                              {isAdd && (
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                  <label className="flex items-center gap-1">Approved payout $
                                    <input type="number" step="any" min="0" className="w-24 rounded border border-slate-300 px-2 py-1" data-testid={`wo-payroll-payout-${w.worker_key}`}
                                      value={f.approved_payout} onChange={(e) => setPf(w.worker_key, { approved_payout: e.target.value })} /></label>
                                  <input placeholder="Basis (optional)" className="w-40 rounded border border-slate-300 px-2 py-1" data-testid={`wo-payroll-basis-${w.worker_key}`}
                                    value={f.payout_basis} onChange={(e) => setPf(w.worker_key, { payout_basis: e.target.value })} />
                                  <select className="rounded border border-slate-300 px-2 py-1" data-testid={`wo-payroll-timing-${w.worker_key}`}
                                    value={f.payment_timing} onChange={(e) => setPf(w.worker_key, { payment_timing: e.target.value })}>
                                    <option value="next_payroll">Next Payroll</option>
                                    <option value="specified_future">Specified Future Payroll</option>
                                    <option value="after_customer_payment">After Customer Payment Is Received</option>
                                  </select>
                                  {f.payment_timing === 'specified_future' && (
                                    <span className="flex items-center gap-1">
                                      <select className="rounded border border-slate-300 px-2 py-1" data-testid={`wo-payroll-group-${w.worker_key}`}
                                        value={f.specified_group} onChange={(e) => setPf(w.worker_key, { specified_group: e.target.value })}>
                                        <option value="first_half">First Half</option>
                                        <option value="second_half">Second Half</option>
                                        <option value="monthly">Monthly</option>
                                      </select>
                                      <input type="number" className="w-16 rounded border border-slate-300 px-2 py-1" value={f.specified_year} onChange={(e) => setPf(w.worker_key, { specified_year: e.target.value })} data-testid={`wo-payroll-year-${w.worker_key}`} />
                                      <input type="number" min="1" max="12" className="w-14 rounded border border-slate-300 px-2 py-1" value={f.specified_month} onChange={(e) => setPf(w.worker_key, { specified_month: e.target.value })} data-testid={`wo-payroll-month-${w.worker_key}`} />
                                    </span>
                                  )}
                                </div>
                              )}
                              {!isAdd && <div className="mt-1 text-[11px] text-slate-500">Hours recorded; no payroll pay item will be created.</div>}
                            </div>
                          );
                        })}
                        <div className="flex items-center gap-2">
                          <Button size="sm" className="bg-[#0E2A47]" onClick={submitPayroll} disabled={payrollBusy || payrollPrev.missing?.length > 0} data-testid="wo-payroll-submit-btn">Submit to Payroll</Button>
                          <Button size="sm" variant="outline" onClick={() => setPayrollPrev(null)} data-testid="wo-payroll-cancel-btn">Cancel</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* ============ 5 · FINISH & SEND ============ */}
          <Card className="border-slate-200 shadow-sm rounded-2xl" data-testid="wo-section-finish-send">
            <CardHeader className="bg-[#0E2A47] rounded-t-2xl px-4 py-2.5"><CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white flex items-center gap-2"><CheckCircle2 size={15} /> 5 · Finish &amp; Send</CardTitle></CardHeader>
            <CardContent className="space-y-5" data-testid="wo-field-documents">
              {/* Administrative closeout — an explicit, consequential completion action */}
              {canEdit && wo.status === 'Awaiting Administrative Closeout' && (
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#8FC93A]/40 bg-[#8FC93A]/10 px-3 py-2" data-testid="wo-closeout-row">
                  <Button size="sm" className="bg-[#8FC93A] text-[#0E2A47] hover:bg-[#7fb82f] font-semibold" onClick={() => { setClose({ billable: false, notes: '' }); setDialog('closeout'); }} data-testid="wo-closeout-btn"><CheckCircle2 size={14} className="mr-1.5" />Complete Work Order</Button>
                  <span className="text-xs text-slate-500">Administrative closeout. This is separate from editing details.</span>
                </div>
              )}
              {/* Completion summary */}
              {(wo.work_performed || wo.completion_status) ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field icon={Wrench} label="Completion Status">{val(wo.completion_status)}</Field>
                  <Field icon={ClipboardList} label="Work Performed"><span data-testid="wo-work-performed">{val(wo.work_performed)}</span></Field>
                  {wo.initial_condition_notes && <Field icon={AlertTriangle} label="Initial Condition (on arrival)"><span data-testid="wo-initial-condition">{wo.initial_condition_notes}</span></Field>}
                  {wo.completion_notes && <Field label="Completion Notes">{wo.completion_notes}</Field>}
                  {wo.follow_up_required && <Field icon={AlertTriangle} label="Follow-up">{wo.follow_up_explanation || 'Required'}</Field>}
                </div>
              ) : (!wo.completed_at && !wo.work_started_at) && (
                <div className="text-sm text-slate-400">This work order is not yet completed. Completion details, the report and customer delivery become available once field work is finished and closed out.</div>
              )}

              {/* Personnel closeout summary / action */}
              {canManage && (wo.work_performed_by ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="wo-closeout-summary">
                  <div><span className="text-slate-400 text-xs block">Work Performed By</span>{wo.work_performed_by}</div>
                  <div><span className="text-slate-400 text-xs block">Closeout Recorded By</span>{wo.closeout_recorded_by || '—'}</div>
                  <div><span className="text-slate-400 text-xs block">Closeout Verified By</span>{wo.verified_by_name || '—'} ({wo.verification_method_closeout || '—'})</div>
                  <div><span className="text-slate-400 text-xs block">Actual Service</span>{fmtDate(wo.actual_service_date) || '—'} {wo.actual_start_time ? `· ${wo.actual_start_time}–${wo.actual_end_time || ''}` : (wo.actual_total_hours ? `· ${wo.actual_total_hours}h` : '')}</div>
                </div>
              ) : !woLocked && (
                <Button size="sm" className="bg-[#0E2A47]" onClick={() => { setCloseout((c) => ({ ...c, work_performed_by: wo.assigned_to_name || '' })); setDialog('closeout-personnel'); }} data-testid="wo-personnel-closeout-btn">
                  <CheckCircle2 size={14} className="mr-1.5" /> Record Personnel Closeout
                </Button>
              ))}

              {/* On-site feedback */}
              {(wo.feedback_courtesy || wo.feedback_communication || wo.feedback_resolution || wo.feedback_comments) && (
                <div className="border-t pt-3" data-testid="wo-feedback">
                  <div className="text-slate-400 text-xs mb-1">On-site Feedback</div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-700">
                    <span>Courtesy: <b>{wo.feedback_courtesy || '—'}/5</b></span>
                    <span>Communication: <b>{wo.feedback_communication || '—'}/5</b></span>
                    <span>Resolution: <b>{wo.feedback_resolution || '—'}/5</b></span>
                  </div>
                  {wo.feedback_comments && <div className="text-sm text-slate-500 italic mt-1">“{wo.feedback_comments}”</div>}
                </div>
              )}

              {/* Regenerate & Preview (Operations / Admin) — regenerates the PDF for review only */}
              {canManage && (
                <div className="border-t pt-3 flex flex-wrap items-center gap-3" data-testid="wo-regen-row">
                  {wo.status !== 'Completed' && (
                    <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-0.5" data-testid="wo-draft-report-lang">
                      <button type="button" onClick={() => setReportLang('en')} data-testid="wo-draft-lang-en"
                        className={`rounded-md px-2.5 py-1 text-xs font-semibold ${reportLang === 'en' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`}>EN</button>
                      <button type="button" onClick={() => setReportLang('es')} data-testid="wo-draft-lang-es"
                        className={`rounded-md px-2.5 py-1 text-xs font-semibold ${reportLang === 'es' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`}>ES</button>
                    </div>
                  )}
                  <Button size="sm" className="bg-[#0E2A47]" onClick={regenerateReport} data-testid="wo-regen-preview-btn">
                    <FileText size={14} className="mr-1.5" /> Regenerate &amp; Preview
                  </Button>
                  {wo.status !== 'Completed' && (
                    <Button size="sm" variant="outline" className="h-8" onClick={viewDraftReport} data-testid="wo-draft-report-btn">
                      <FileText size={14} className="mr-1.5" /> View Draft Report
                    </Button>
                  )}
                  <div className="text-xs text-slate-400">
                    {wo.completion_report_generated_at
                      ? `Last generated ${fmt(wo.completion_report_generated_at, true)}`
                      : 'Not yet generated'}
                    <span className="ml-2 text-slate-300">· regenerates the PDF for review; sending to the client is a separate step below</span>
                  </div>
                </div>
              )}

              {/* Completion Report — download / send to customer */}
              {wo.status === 'Completed' && (
                <div data-testid="wo-completion-report-card" className="rounded-2xl border border-[#8FC93A]/40 overflow-hidden">
                  <div className="bg-[#0E2A47] px-5 py-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#8FC93A]/20 text-[#A3D65C]"><FileText size={20} /></div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A3D65C]">ROUT · by TFS</div>
                      <div className="text-base font-bold text-white">Completion Report</div>
                    </div>
                  </div>
                  <div className="space-y-4 p-5">
                    <p className="text-sm leading-relaxed text-slate-600">
                      A professional, customer-facing record of this completed service — work performed, before/after photos,
                      on-site verification and any feedback — suitable for your records.
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      <div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Work Order</div><div className="text-sm font-mono font-semibold text-[#0E2A47]">{val(wo.tfs_work_order_number)}</div></div>
                      <div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Completed</div><div className="text-sm font-medium text-slate-700">{fmt(wo.completed_at)}</div></div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-0.5" data-testid="wo-report-lang">
                        <button type="button" onClick={() => setReportLang('en')} data-testid="wo-report-lang-en"
                          className={`rounded-md px-2.5 py-1 text-xs font-semibold ${reportLang === 'en' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`}>EN</button>
                        <button type="button" onClick={() => setReportLang('es')} data-testid="wo-report-lang-es"
                          className={`rounded-md px-2.5 py-1 text-xs font-semibold ${reportLang === 'es' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`}>ES</button>
                      </div>
                      <Button className="bg-[#8FC93A] text-[#0E2A47] hover:bg-[#7fb82f] font-semibold" onClick={downloadReport} data-testid="wo-download-report-btn">
                        <Download size={15} className="mr-1.5" /> Download Report (PDF)
                      </Button>
                      {canManage && (
                        <Button variant="outline" className="border-[#0E2A47]/20 text-[#0E2A47] hover:bg-slate-50" onClick={openNotify} data-testid="wo-send-report-btn">
                          <Send size={15} className="mr-1.5" /> Send to Customer
                        </Button>
                      )}
                    </div>
                    {wo.customer_notified ? (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="wo-notified-badge">
                        <div className="flex items-center gap-1.5 font-semibold text-emerald-700"><BadgeCheck size={15} /> {canManage ? 'Customer Notified' : 'Delivered by TFS'}</div>
                        <div className="mt-1 text-xs text-emerald-800/80 space-y-0.5">
                          {canManage
                            ? <div>To: {(wo.customer_notification_recipients || []).join(', ') || '—'}</div>
                            : <div>Recipient: {(wo.customer_notification_recipients || []).join(', ') || 'Authorized client contact'}</div>}
                          <div>Method: {wo.customer_notification_method || 'Email'}{canManage ? ` · Status: ${wo.customer_notification_status || '—'}` : ''}</div>
                          <div>{fmt(wo.customer_notified_at, true)}{canManage && wo.customer_notified_by_name ? ` · by ${wo.customer_notified_by_name}` : ''}</div>
                        </div>
                      </div>
                    ) : (
                      canManage
                        ? <div className="text-xs text-amber-700" data-testid="wo-not-notified">Customer has not been notified yet.</div>
                        : <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800" data-testid="wo-report-available">
                            This completion report is available for viewing and download. It has not yet been delivered by TFS.
                          </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity History moved to an on-demand drawer (banner button) */}
      </div>

      {/* Activity History — on demand */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg" data-testid="wo-history-dialog">
          <DialogHeader><DialogTitle>Activity History</DialogTitle></DialogHeader>
          <ol className="relative border-l border-gray-200 ml-2 space-y-5 max-h-[65vh] overflow-y-auto pr-2" data-testid="wo-activity-timeline">
            {activity.map((a) => (
              <li key={a.id} className="ml-4">
                <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-[#A3D65C] border-2 border-white" />
                <div className="text-sm font-medium text-slate-800">{ACTION_LABELS[a.action] || a.message || a.action}</div>
                <div className="text-xs text-slate-400">{a.actor_name} · {roleLabel(a.actor_role)}</div>
                <div className="text-xs text-slate-400">{fmt(a.timestamp, true)}</div>
                {a.message && <div className="text-xs text-slate-500 mt-1 italic">“{a.message}”</div>}
              </li>
            ))}
            {activity.length === 0 && <li className="ml-4 text-sm text-slate-400">No activity recorded.</li>}
          </ol>
        </DialogContent>
      </Dialog>

      {/* Details — requester / contact / authorization / intake (kept accessible, not on the page) */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg" data-testid="wo-details-dialog">
          <DialogHeader><DialogTitle>Work Order Details</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 text-sm max-h-[65vh] overflow-y-auto">
            <Field icon={User} label="Requester">{val(wo.requestor_name)}</Field>
            <Field icon={User} label="Site Contact">{val(wo.main_contact_name || wo.contact_person)}</Field>
            <Field icon={FileText} label="Contact Phone">{val(wo.main_contact_phone || wo.contact_phone)}</Field>
            <Field icon={FileText} label="Contact Email">{val(wo.main_contact_email || wo.contact_email)}</Field>
            <Field icon={BadgeCheck} label="Authorization">{val(wo.authorization_method)}</Field>
            <Field icon={ClipboardList} label="Intake Source">{val(wo.intake_source || wo.source_type)}</Field>
            <Field icon={Receipt} label="Client PO">{val(wo.customer_po_number)}</Field>
            <Field icon={Receipt} label="Client Reference">{val(wo.client_reference_number || wo.customer_reference)}</Field>
            <Field icon={FileText} label="Service Request">{val(wo.rout_service_request_number)}</Field>
            <Field icon={Building2} label="Client Location Code">{val(wo.client_location_code)}</Field>
          </div>
        </DialogContent>
      </Dialog>

      {/* Priority dialog */}
      <Dialog open={dialog === 'priority'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-priority-dialog">
          <DialogHeader><DialogTitle>Change Priority</DialogTitle></DialogHeader>
          <Select value={pr} onValueChange={setPr}>
            <SelectTrigger data-testid="wo-priority-select"><SelectValue placeholder="Select priority" /></SelectTrigger>
            <SelectContent>{PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={busy || !pr} data-testid="wo-priority-confirm"
              onClick={() => post('priority', { priority_level: pr }, 'Priority updated')}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment dialog */}
      <Dialog open={dialog === 'assignment'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-assignment-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Assignment</DialogTitle></DialogHeader>
          {renderAssignmentEditor()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={busy} data-testid="wo-assignment-confirm"
              onClick={saveAssignment}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch dialog */}
      <Dialog open={dialog === 'dispatch'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-dispatch-dialog">
          <DialogHeader><DialogTitle>Dispatch Work Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              Assigned to <span className="font-semibold text-[#0E2A47]">{wo?.assigned_to_name || '—'}</span>
              {(wo?.assigned_personnel_has_rout || wo?.assigned_to)
                ? ' · has ROUT access — will be dispatched electronically via the Field Portal.'
                : ' · no ROUT access — record a manual notification below.'}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Scheduled date</Label><Input type="date" value={disp.scheduled_date} onChange={(e) => setDisp((d) => ({ ...d, scheduled_date: e.target.value }))} data-testid="wo-dispatch-date" /></div>
              <div><Label className="text-xs">Service window</Label><Input placeholder="e.g., Tonight 8–10pm" value={disp.service_window} onChange={(e) => setDisp((d) => ({ ...d, service_window: e.target.value }))} data-testid="wo-dispatch-window" /></div>
            </div>
            {!(wo?.assigned_personnel_has_rout || wo?.assigned_to) && (
              <div>
                <Label className="text-xs">Notification Method *</Label>
                <Select value={disp.dispatch_method} onValueChange={(v) => setDisp((d) => ({ ...d, dispatch_method: v }))}>
                  <SelectTrigger data-testid="wo-dispatch-method"><SelectValue placeholder="How was the assignee notified?" /></SelectTrigger>
                  <SelectContent>{DISPATCH_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Operations comment {!(wo?.assigned_personnel_has_rout || wo?.assigned_to) ? '*' : '(optional)'}</Label>
              <Textarea rows={2} value={disp.notes} onChange={(e) => setDisp((d) => ({ ...d, notes: e.target.value }))}
                placeholder="e.g., Employee assigned this work order and scheduled to perform the service tonight." data-testid="wo-dispatch-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={busy} data-testid="wo-dispatch-confirm" onClick={submitDispatch}>Dispatch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-creation assignment prompt (Open - Unassigned) */}
      <Dialog open={dialog === 'created-unassigned'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-created-unassigned-dialog">
          <DialogHeader><DialogTitle>Work Order Created Successfully</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            This Work Order is currently <span className="font-semibold text-[#0E2A47]">Open – Unassigned</span>. Would you like to assign it now?
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="wo-assign-notnow-btn">Not Now</Button>
            <Button className="bg-[#8FC93A] text-[#0E2A47] hover:bg-[#7fb82f] font-semibold" data-testid="wo-assign-now-btn"
              onClick={openAssignment}>
              Assign Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Personnel closeout dialog */}
      <Dialog open={dialog === 'closeout-personnel'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-personnel-closeout-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Record Personnel Closeout</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Recorded by Operations on behalf of the assigned employee. Requires before &amp; after photos (upload them first). Actual time is stored separately from any payout.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Work Performed By *</Label><Input value={closeout.work_performed_by} onChange={(e) => setCloseout((c) => ({ ...c, work_performed_by: e.target.value }))} data-testid="closeout-performed-by" /></div>
              <div><Label className="text-xs">Verified By</Label><Input value={closeout.verified_by_name} onChange={(e) => setCloseout((c) => ({ ...c, verified_by_name: e.target.value }))} data-testid="closeout-verified-by" /></div>
              <div><Label className="text-xs">Actual Service Date *</Label><Input type="date" value={closeout.actual_service_date} onChange={(e) => setCloseout((c) => ({ ...c, actual_service_date: e.target.value }))} data-testid="closeout-date" /></div>
              <div>
                <Label className="text-xs">Verification Method *</Label>
                <Select value={closeout.verification_method} onValueChange={(v) => setCloseout((c) => ({ ...c, verification_method: v }))}>
                  <SelectTrigger data-testid="closeout-verification"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{['Photo Documentation', 'Operations Confirmation', 'Client Sign-off', 'Other'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Start Time</Label><Input type="time" value={closeout.actual_start_time} onChange={(e) => setCloseout((c) => ({ ...c, actual_start_time: e.target.value }))} data-testid="closeout-start" /></div>
              <div><Label className="text-xs">End Time</Label><Input type="time" value={closeout.actual_end_time} onChange={(e) => setCloseout((c) => ({ ...c, actual_end_time: e.target.value }))} data-testid="closeout-end" /></div>
              <div><Label className="text-xs">Or Verified Total Hours</Label><Input type="number" step="any" value={closeout.actual_total_hours} onChange={(e) => setCloseout((c) => ({ ...c, actual_total_hours: e.target.value }))} data-testid="closeout-hours" /></div>
            </div>
            <div><Label className="text-xs">Work Performed *</Label><Textarea rows={2} value={closeout.work_performed} onChange={(e) => setCloseout((c) => ({ ...c, work_performed: e.target.value }))} data-testid="closeout-work" /></div>
            <div><Label className="text-xs">Completion Notes</Label><Textarea rows={2} value={closeout.closeout_notes} onChange={(e) => setCloseout((c) => ({ ...c, closeout_notes: e.target.value }))} data-testid="closeout-notes" /></div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={closeout.follow_up_required} onChange={(e) => setCloseout((c) => ({ ...c, follow_up_required: e.target.checked }))} data-testid="closeout-followup" /> Follow-up required
            </label>
            {closeout.follow_up_required && <div><Label className="text-xs">Follow-up Date</Label><Input type="date" value={closeout.follow_up_date} onChange={(e) => setCloseout((c) => ({ ...c, follow_up_date: e.target.value }))} data-testid="closeout-followup-date" /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={busy} onClick={submitCloseout} data-testid="closeout-submit">Submit Closeout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing disposition dialog */}
      <Dialog open={dialog === 'billing-disposition'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-billing-disposition-dialog">
          <DialogHeader><DialogTitle>Billing Disposition</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Above-Scope classification does not determine payout or client billing. "Billable — Pricing Required" holds the record (not sent to accounting).</p>
            <Select value={dispo.disposition} onValueChange={(v) => setDispo((d) => ({ ...d, disposition: v }))}>
              <SelectTrigger data-testid="dispo-select"><SelectValue placeholder="Select disposition" /></SelectTrigger>
              <SelectContent>{['Billable — Pricing Confirmed', 'Billable — Pricing Required', 'Included / No Additional Client Charge', 'Non-Billable Correction or Warranty', 'Other — Reason Required'].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <div><Label className="text-xs">Reason {dispo.disposition === 'Other — Reason Required' ? '*' : '(optional)'}</Label>
              <Textarea rows={2} value={dispo.reason} onChange={(e) => setDispo((d) => ({ ...d, reason: e.target.value }))} data-testid="dispo-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={busy} onClick={submitDispo} data-testid="dispo-submit">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify customer dialog */}
      <Dialog open={dialog === 'notify'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="wo-notify-dialog">
          <DialogHeader><DialogTitle>Send Completion Report to Customer</DialogTitle></DialogHeader>
          {notify.loading || !notify.preview ? (
            <div className="py-8 text-center text-sm text-slate-400">Preparing…</div>
          ) : (() => {
            const p = notify.preview; const ev = p.evidence || {}; const ref = p.references || {};
            const canDeliver = p.delivery?.configured;
            const evidenceOk = ev.can_send || (!ev.met && notify.waiver.trim());
            return (
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="wo-notify-refs">
                <b>WO</b> {ref.tfs_work_order_number || '—'} · <b>SR</b> {ref.service_request_number || '—'} · <b>Client Ref</b> {ref.client_reference_number || '—'}
              </div>
              {/* Evidence gate */}
              {ev.can_send ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="wo-notify-evidence-ok">
                  Evidence attached: {ev.before} before / {ev.after} after photo(s){ev.exception_approved ? ' · exception approved' : ''}.
                </div>
              ) : (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-2" data-testid="wo-notify-evidence-missing">
                  <div><b>Photos required before delivery.</b> Missing {(ev.missing || []).join(' and ')} (need {ev.required_each} before + {ev.required_each} after).</div>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => { setDialog(null); document.querySelector('[data-testid="wo-section-photos-documents"]')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="wo-notify-addphotos">Add photos</Button>
                  {canEdit && (
                    <div>
                      <Label className="text-[11px] text-amber-900">Global-Admin exception reason (waives the photo requirement)</Label>
                      <Input value={notify.waiver} onChange={(e) => setNotify((n) => ({ ...n, waiver: e.target.value }))}
                        placeholder="Reason a completion photo is unavailable" data-testid="wo-notify-waiver" />
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs">To (client requester)</Label>
                <Input value={notify.to} onChange={(e) => setNotify((n) => ({ ...n, to: e.target.value }))}
                  placeholder="requester@client.com" data-testid="wo-notify-recipients" />
              </div>
              <div>
                <Label className="text-xs">Cc (authorizer / forwarder)</Label>
                <Input value={notify.cc} onChange={(e) => setNotify((n) => ({ ...n, cc: e.target.value }))}
                  placeholder="authorizer@client.com" data-testid="wo-notify-cc" />
              </div>
              {(p.recipients?.missing || []).length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800" data-testid="wo-notify-missing-contacts">
                  {(p.recipients.missing).join(' · ')}. Add the address above if you have it.
                </div>
              )}
              <div>
                <Label className="text-xs">Subject *</Label>
                <Input value={notify.subject} onChange={(e) => setNotify((n) => ({ ...n, subject: e.target.value }))} data-testid="wo-notify-subject" />
              </div>
              <div>
                <Label className="text-xs">Message * (editable)</Label>
                <Textarea rows={9} value={notify.message} onChange={(e) => setNotify((n) => ({ ...n, message: e.target.value }))} data-testid="wo-notify-message" />
              </div>
              <div>
                <Label className="text-xs">Recipient report language</Label>
                <div className="mt-1 flex gap-2">
                  <button type="button" onClick={() => setReportLang('en')} data-testid="wo-notify-lang-en"
                    className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${reportLang === 'en' ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-300 text-[#0E2A47]'}`}>English</button>
                  <button type="button" onClick={() => setReportLang('es')} data-testid="wo-notify-lang-es"
                    className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${reportLang === 'es' ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-300 text-[#0E2A47]'}`}>Español</button>
                  <span className="self-center text-[11px] text-slate-400">The attached PDF will be generated in this language.</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-400">The corrected ROUT Completion Report PDF is attached. Replies route to {p.delivery?.reply_to} (owner copy {p.delivery?.owner_copy}).</p>
              {!canDeliver && (
                <div className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-xs text-slate-700" data-testid="wo-notify-delivery-state">
                  <b>Delivery not configured.</b> Provider: {p.delivery?.provider}. The email will be prepared and recorded but <b>not sent</b>, and this work order will <b>not</b> be marked delivered until the company mailbox ({p.delivery?.sender}) is configured.
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
                <Button className="bg-[#0E2A47] text-white" data-testid="wo-notify-confirm"
                  disabled={busy || !notify.to.trim() || !notify.subject.trim() || !notify.message.trim() || !evidenceOk}
                  onClick={sendNotify}>{canDeliver ? 'Send to Customer' : 'Prepare (delivery off)'}</Button>
              </DialogFooter>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Closeout dialog */}
      <Dialog open={dialog === 'closeout'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-closeout-dialog">
          <DialogHeader><DialogTitle>Complete Work Order (Administrative Closeout)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Office review of the completed documentation (not a physical QC). Confirm the required documentation is present, then complete the work order.</p>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={close.billable} data-testid="wo-closeout-billable"
                onChange={(e) => setClose({ ...close, billable: e.target.checked })} />
              Billable — mark Ready for Billing
            </label>
            <Textarea rows={2} placeholder="Closeout notes (optional)" value={close.notes}
              onChange={(e) => setClose({ ...close, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#8FC93A] text-[#0E2A47] font-semibold" disabled={busy} data-testid="wo-closeout-confirm"
              onClick={() => post('closeout', close, 'Work Order completed')}>Complete Work Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={dialog === 'schedule'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-schedule-dialog">
          <DialogHeader><DialogTitle>Schedule Work Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Scheduled date</Label><Input type="date" value={sch.scheduled_date} onChange={(e) => setSch({ ...sch, scheduled_date: e.target.value })} data-testid="wo-sched-date" /></div>
            <div><Label>Service window / arrival</Label><Input placeholder="e.g., 9–11 AM" value={sch.service_window} onChange={(e) => setSch({ ...sch, service_window: e.target.value })} data-testid="wo-sched-window" /></div>
            <div><Label>Required completion date</Label><Input type="date" value={sch.required_completion_date} onChange={(e) => setSch({ ...sch, required_completion_date: e.target.value })} data-testid="wo-sched-required" /></div>
            <div><Label>Scheduling notes</Label><Textarea rows={2} value={sch.scheduling_notes} onChange={(e) => setSch({ ...sch, scheduling_notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={busy || !sch.scheduled_date} data-testid="wo-schedule-confirm"
              onClick={() => post('schedule', sch, 'Scheduled')}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status dialog */}
      <Dialog open={dialog === 'status'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-status-dialog">
          <DialogHeader><DialogTitle>Change Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Select value={sts.status} onValueChange={(v) => setSts((s) => ({ ...s, status: v }))}>
              <SelectTrigger data-testid="wo-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea rows={3} placeholder="Optional note" value={sts.notes}
              onChange={(e) => setSts((s) => ({ ...s, notes: e.target.value }))} data-testid="wo-status-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={busy || !sts.status} data-testid="wo-status-confirm"
              onClick={() => post('status', { status: sts.status, notes: sts.notes || null }, 'Status updated')}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Billing (Global / General Administrator) */}
      <Dialog open={dialog === 'costs'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="wo-costs-dialog">
          <DialogHeader><DialogTitle>Client Billing</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Client Charge ($)</Label>
              <div className="mt-1 mb-2 flex gap-2">
                {[['pre_established', 'Preset pricing'], ['one_time', 'One-time pricing']].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setCostForm({ ...costForm, service_fee_type: v })}
                    data-testid={`fee-type-${v}`}
                    className={`rounded-lg border px-3 py-1.5 text-sm ${costForm.service_fee_type === v ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-200 text-slate-600'}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <Input type="number" step="0.01" min="0" value={costForm.client_charge}
                onChange={(e) => setCostForm({ ...costForm, client_charge: e.target.value })} data-testid="cost-client-charge-input" />
              <p className="mt-1 text-[11px] text-slate-400">The single agreed service charge to the client. Preset pricing may populate this; one-time pricing is entered directly. Enter it once here.</p>
            </div>
            <div>
              <Label>Additional Charges ($) — only if genuinely separate</Label>
              <Input type="number" step="0.01" min="0" value={costForm.additional_charges}
                onChange={(e) => setCostForm({ ...costForm, additional_charges: e.target.value })} data-testid="cost-additional-input" />
            </div>
            <div>
              <Label>Additional Charges Note</Label>
              <Textarea value={costForm.additional_charges_note} onChange={(e) => setCostForm({ ...costForm, additional_charges_note: e.target.value })} />
            </div>
            <div className="rounded-lg bg-[#8FC93A]/10 border border-[#8FC93A]/30 px-3 py-2 text-sm flex justify-between">
              <span className="font-semibold text-[#0E2A47]">Total Client Charge</span>
              <span className="font-extrabold text-[#0E2A47]" data-testid="cost-total-preview">
                {`$${(Number(costForm.client_charge || 0) + Number(costForm.additional_charges || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={saveCosts} disabled={busy} data-testid="cost-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Materials & Supplies editor */}
      <Dialog open={dialog === 'materials'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wo-materials-dialog">
          <DialogHeader><DialogTitle>Materials &amp; Supplies</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={matStatus === 'none_used'} data-testid="mat-none-used"
                onChange={(e) => setMatStatus(e.target.checked ? 'none_used' : (mats.length ? 'entered' : ''))} />
              No materials used for this work order
            </label>
            {matStatus !== 'none_used' && (
              <>
                {mats.map((m, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`mat-row-${i}`}>
                    <div className="flex gap-2">
                      <Input placeholder="Description" value={m.description} onChange={(e) => updMat(i, 'description', e.target.value)} data-testid={`mat-desc-${i}`} />
                      <Button variant="ghost" className="text-red-500 shrink-0" onClick={() => rmMat(i)}>Remove</Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Input type="number" step="0.01" placeholder="Qty" value={m.quantity} onChange={(e) => updMat(i, 'quantity', e.target.value)} data-testid={`mat-qty-${i}`} />
                      <Input type="number" step="0.01" placeholder="Unit $" value={m.unit_cost} onChange={(e) => updMat(i, 'unit_cost', e.target.value)} data-testid={`mat-unit-${i}`} />
                      <div className="flex items-center justify-end px-2 text-sm text-slate-500">= {matLineTotal(m) != null ? `$${matLineTotal(m).toFixed(2)}` : '—'}</div>
                      <Input type="date" placeholder="Purchased" value={m.purchase_date} onChange={(e) => updMat(i, 'purchase_date', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input placeholder="Vendor / Store" value={m.vendor} onChange={(e) => updMat(i, 'vendor', e.target.value)} />
                      <Input placeholder="Notes" value={m.notes} onChange={(e) => updMat(i, 'notes', e.target.value)} />
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      <label className="flex items-center gap-2">Receipt
                        <input type="file" accept="image/*" onChange={(e) => uploadMatPhoto(i, 'receipt', e.target.files?.[0])} />
                        {m.receipt_photo_id && <span className="text-emerald-600">✓</span>}</label>
                      <label className="flex items-center gap-2">Material photo
                        <input type="file" accept="image/*" onChange={(e) => uploadMatPhoto(i, 'material', e.target.files?.[0])} />
                        {m.material_photo_id && <span className="text-emerald-600">✓</span>}</label>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addMatRow} data-testid="mat-add-row">+ Add material</Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={saveMaterials} disabled={busy} data-testid="mat-save-btn">Save Materials</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ovr} onOpenChange={(o) => !o && setOvr(null)}>
        <DialogContent data-testid="wo-override-dialog">
          <DialogHeader><DialogTitle>Override Customer-Approved Pricing / Scope</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Current approved Fixed Price</span><b className="text-[#0E2A47]">{money(wo?.customer_billing_amount ?? wo?.pev_approved_fixed_price)}</b></div>
              <div className="mt-1 text-xs text-slate-500">Original customer-approved: {money(wo?.pev_approved_fixed_price)} — this is never erased.</div>
            </div>
            <div><Label className="text-xs">Revised Fixed Price ($) — optional</Label>
              <Input type="number" step="0.01" min="0" value={ovr?.new_amount ?? ''} onChange={(e) => setOvr((o) => ({ ...o, new_amount: e.target.value }))} data-testid="wo-override-amount" placeholder={String(wo?.customer_billing_amount ?? '')} /></div>
            <div><Label className="text-xs">Revised Scope — optional</Label>
              <Textarea value={ovr?.new_scope ?? ''} onChange={(e) => setOvr((o) => ({ ...o, new_scope: e.target.value }))} data-testid="wo-override-scope" placeholder={wo?.service_scope || ''} /></div>
            <div><Label className="text-xs">Written explanation (required)</Label>
              <Textarea value={ovr?.reason ?? ''} onChange={(e) => setOvr((o) => ({ ...o, reason: e.target.value }))} data-testid="wo-override-reason" placeholder="Why is this override necessary?" /></div>
            <p className="text-[11px] text-amber-700">A material change flags the item for renewed customer authorization. Your identity, the original and revised terms, and this reason are recorded in the audit history.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOvr(null)} data-testid="wo-override-cancel">Cancel</Button>
            <Button className="bg-[#0E2A47]" disabled={ovrBusy || !ovr?.reason?.trim()} onClick={submitOverride} data-testid="wo-override-submit">{ovrBusy ? 'Recording…' : 'Record Override'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderDetail;
